import { getSiteOrigin } from "@/lib/siteUrl";
import { getVolumeBotLandingConfigByPath } from "@/config/volumeBotLandingConfig";
import { IDX_EXCHANGE_URL } from "@/config/idxExchangeAppConfig";
import { IDX_TELEGRAM_BOT_URL } from "@/config/idxSocialLinks";

const siteOrigin = getSiteOrigin();

export const TOOL_LEARN_MORE_CATALOG = {
  "volume-bot": {
    key: "volume-bot",
    toolName: "Solana Volume Bot",
    toolPath: "/volume-bot",
    learnMorePath: "/volume-bot/learn-more",
    category: "Growth Automation",
    primaryKeyword: "solana volume bot",
    seoTitle: "Solana Volume Bot Guide | Engines & DEX Pools",
    pageHeading: "Solana Volume Bot Guide",
    metaDescription:
      "IDX Solana volume bot guide: pick a DEX landing, choose V4–V12 engines, fund SOL, and run on-chain campaigns with clear expectations.",
    description:
      "IDX volume bots automate on-chain swaps across supported Solana pools so teams can add chart activity during launch windows — after liquidity exists and the right bot landing is chosen.",
    summary:
      "Choose the right bot landing, match an engine to your launch stage, fund on-chain automation, and monitor results — with clear notes on liquidity, cashback, and DexScreener visibility.",
    primaryUseCase:
      "Use a Solana volume bot when your mint already trades on a supported pool and you want structured on-chain activity during launch, relaunch, or post-graduation visibility windows.",
    audience: [
      "Token founders launching on Solana",
      "IDX Launchlab creators boosting curve activity",
      "Growth teams sustaining volume after pool graduation",
    ],
    steps: [
      "Pick the landing that fits your token — general hub, DEX-specific page, or IDX Launchlab bot.",
      "Connect wallet, paste mint address, and confirm token symbol.",
      "Select up to three engines (V12, V10, V4, V4 Eco, V6, V8, Pair, Mini Buy, Mini Buy/Sell) for your budget and stage. Use Pair when you want to trade your token with a second token in one bot.",
      "Recharge in SOL, start from dashboard, monitor trades, and stop or refund unused gas anytime.",
    ],
    landingPaths: [
      {
        href: "/pair-bot",
        label: "Pair bot",
        description:
          "Trade your token with a second token in one bot, so both charts get on-chain volume.",
      },
      {
        href: "/volume-bot",
        label: "General volume bot",
        description:
          "Multi-DEX hub for Pump.fun, Raydium, Orca, Meteora, LaunchLab, Moonit, FluxBags, Bags, and more.",
        icons: [
          "/pools/pumpfun.png",
          "/pools/raydium.png",
          "/pools/orca.png",
          "/pools/meteora.png",
          "/pools/idx.png",
          "/pools/launchlab.png",
          "/pools/moonit.png",
          "/pools/fluxbeam.png",
          "/pools/bags.png",
        ],
      },
      {
        href: "/idx-volume-bot",
        label: "IDX Launchlab bot",
        description:
          "Built for coins launched on IDX Launchlab — boost visibility and earn creator fees on bot-driven trades.",
        icon: "/pools/idx.png",
      },
      {
        href: "/pumpfun-volume-bot",
        label: "Pump.fun bot",
        description: "Bonding curve through PumpSwap and post-graduation pools.",
        icon: "/pools/pumpfun.png",
      },
      {
        href: "/raydium-volume-bot",
        label: "Raydium bot",
        description: "AMM and concentrated liquidity pairs on Raydium.",
        icon: "/pools/raydium.png",
      },
    ],
    engineGuide: {
      title: "Which engine should you pick?",
      intro:
        "Engines differ in trade frequency, maker count, and SOL spend. Match activity to your lifecycle — you can run up to three engines on one launch. V12, V10, and V8 push hard; V6/V4/V4 Eco suit steadier budgets; Mini Buy and Mini Buy/Sell add dense micro-swap layers beside a primary engine.",
      engines: [
        {
          id: "V12",
          label: "V12 and V8 — aggressive visibility",
          body: "V12 is the top ranking push (~4 SOL/hr, instant trades, very high maker count; often paired with Mini Buy/Sell). V8 is high activity (~0.5 SOL/hr, 3000+ SOL/day, 4000+ makers/day, trades every 15–30 sec) for launch windows and post-graduation chart pressure without full V12 spend.",
        },
        {
          id: "V10",
          label: "V10 — continuous high tx",
          body: "Continuous unique-maker flow (~1 SOL/hr, ~3000 SOL/day, unique high tx). First charge 24 SOL. Best when you want a busy tape without V12 spend.",
        },
        {
          id: "V6",
          label: "V6, V4, and V4 Eco — sustained budgets",
          body: "V6 keeps mid-tier cadence (~0.15 SOL/hr). V4 is the light organic boost (~0.045 SOL/hr, ~6–9 min); V4 Eco runs ~7–10 min at a lower hourly burn (~0.032 SOL/hr). Use these when testing spend or supporting thin liquidity.",
        },
        {
          id: "Mini",
          label: "Mini Buy and Mini Buy/Sell",
          body: "Mini Buy stacks bid-side micro purchases; Mini Buy/Sell alternates two-sided micro swaps (~30/min). Layer either with V12 or V8 when you want denser chart texture on top of a primary engine.",
        },
        {
          id: "Pair",
          label: "Pair — two tokens, one bot",
          body: "Pair trades your token with a second token in one bot instead of quoting IDX. Cadence is about every 7–10 minutes at ~0.04 SOL/hour (~0.96 SOL/day), with roughly 80 SOL of volume and 125+ makers counted per token. Open the pair bot landing, paste both mints, and pick a pool for each.",
        },
      ],
    },
    promoCallout: {
      title: "Launchlab creator? Launch first, then boost.",
      body: "Fair-launch on IDX Launchlab’s bonding curve, earn creator fees on every trade, then run a volume bot to accelerate chart activity. Claim earnings anytime from Rewards.",
      href: "/idx-launchlab/create",
      ctaLabel: "Open IDX Launchlab create flow",
    },
    supportedPools: {
      title: "Pools that work well on IDX",
      intro:
        "IDX volume bots execute on-chain through supported Solana DEX pools and launchpads. Dedicated landings cover IDX Launchlab, Pump.fun, Raydium, Orca, and Meteora; Moonit, FluxBags, Bags, and other routes stay on the general hub.",
      pools: [
        {
          id: "idx",
          name: "IDX Launchlab",
          icon: "/pools/idx.png",
          description:
            "Raydium LaunchLab bonding curve — launch, boost, and earn creator fees on every trade.",
          href: "/idx-volume-bot",
          badge: "Creator fees",
        },
        {
          id: "pumpfun",
          name: "Pump.fun",
          icon: "/pools/pumpfun.png",
          description:
            "Bonding curve through PumpSwap and post-graduation pools — includes Pumpfun cashback panel.",
          href: "/pumpfun-volume-bot",
          badge: "Dedicated landing",
        },
        {
          id: "raydium",
          name: "Raydium",
          icon: "/pools/raydium.png",
          description:
            "AMM and concentrated liquidity pairs — strong fit after Launchlab graduation or Raydium listings.",
          href: "/raydium-volume-bot",
          badge: "Dedicated landing",
        },
        {
          id: "orca",
          name: "Orca",
          icon: "/pools/orca.png",
          description:
            "Whirlpool and Orca routes for tokens with established Orca liquidity.",
          href: "/orca-volume-bot",
          badge: "Dedicated landing",
        },
        {
          id: "meteora",
          name: "Meteora",
          icon: "/pools/meteora.png",
          description:
            "DLMM and dynamic pools — useful for tokens trading on Meteora bins.",
          href: "/meteora-volume-bot",
          badge: "Dedicated landing",
        },
      ],
    },
    faq: [
      {
        q: "Which Solana pools and landings does IDX support?",
        a: "Bots work with Pump.fun, Raydium, Orca, Meteora, IDX Launchlab, Moonit, FluxBags, Bags, and related routes. Use a DEX-specific landing when your token mainly trades on one venue; use the general hub for multi-DEX tokens; use the IDX Launchlab bot when you launched there and want creator-fee context.",
      },
      {
        q: "Does a volume bot guarantee DexScreener trending?",
        a: "No. Bots increase on-chain trading activity and transaction count. Rankings still depend on broader market demand. Your token should already trade on a supported pool — create or graduate liquidity first, then fund the bot.",
      },
      {
        q: "How do Launchlab fees and multi-engine runs work?",
        a: "IDX Launchlab creators earn fixed creator fees on trades (0.5% on the curve, then pool fees via Fee Key NFT after graduation). Pump.fun uses market-cap-tier bot fee rebates. You can run up to three engines on one launch with the same mint — IDX creates one bot per engine.",
      },
      {
        q: "What should I monitor during a campaign?",
        a: "Track SOL spend pace, bot status, transaction health, and market response. Pause and refund unused gas from the dashboard if activity or ROI is off target.",
      },
    ],
    intentSections: [
      {
        title: "When volume bots fit a Solana launch",
        body: "IDX volume bots suit launch windows, relaunch visibility pushes, and post-graduation chart activity when you already have baseline liquidity and a clear narrative. Fair-launch on IDX Launchlab first if you want creator fees, then boost from the IDX bot landing so swaps accrue claimable rewards. Avoid heavy spend on weak pools, running bots before a public landing page is ready, or treating automation as a guaranteed trending or fill machine.",
      },
      {
        title: "Campaign hygiene and expectations",
        body: "Set budget expectations upfront, run a short validation window, assign someone to monitor the dashboard daily, and communicate transparently with your community. Pair engine choice to stage — V4/V6 for tests, V8/V10/V12/Mini layers for aggressive windows — and stop bots with unused-gas refunds when the campaign ends.",
      },
    ],
    riskNotes: [
      "Volume automation does not guarantee price action, bonding curve completion, or exchange listings.",
      "Set recharge limits you are comfortable losing to gas and slippage before scaling spend.",
      "Verify mint address and pool support before funding — wrong mint or unsupported routes waste SOL.",
      "Stop bots and refund unused gas from the dashboard when pausing a campaign.",
    ],
    decisionChecklist: [
      "Token mint is verified and trades on a supported pool or Launchlab route.",
      "Correct bot landing is selected (general, DEX-specific, or IDX Launchlab).",
      "Engine choice matches budget and launch stage (V4/V6 vs V8/V10/V12/Mini).",
      "Landing page, socials, and narrative are ready before visibility spikes.",
      "Dashboard owner assigned to monitor spend and bot health daily.",
    ],
    related: [],
    extraRelatedLinks: [
      { href: "/pair-bot", label: "Run a pair bot across two tokens" },
      { href: "/pair-bot/learn-more", label: "Read the Solana pair bot guide" },
      { href: "/idx-volume-bot", label: "Run volume automation on IDX Launchlab coins" },
      { href: "/idx-launchlab/create", label: "Fair-launch on IDX Launchlab before boosting" },
      { href: "/idx-launchlab/rewards", label: "Claim IDX Launchlab creator fee earnings" },
      { href: "/pumpfun-volume-bot", label: "Configure Pump.fun curve and pool volume" },
      { href: "/holders-maker", label: "Grow wallet distribution beside volume campaigns" },
      { href: "/token-hub", label: "Confirm mint context before funding bots" },
      { href: "/liquidity-pool-creator", label: "Add Raydium liquidity before scaling bots" },
    ],
    heroCtaLabel: "Open volume bot hub",
    faqCta: {
      label: "Start Solana volume bot setup",
    },
    heroIllustration: {
      src: "/volume_bot.png",
      alt: "IDX volume bot illustration",
      width: 240,
      height: 390,
    },
    visualBlocks: [
      {
        id: "dashboard",
        title: "Track bots from your dashboard",
        description:
          "Start, stop, recharge, and refund gas from one place. Monitor engine status and bot health while automation runs on-chain across supported Solana DEX pools — so DexScreener activity reflects real swaps, not a browser tab you have to babysit.",
        imageSrc: "/idx-volume-bot-dashboard.png",
        imageAlt:
          "IDX AutoBot dashboard preview — Mini Buy/Sell bot card with gas level, last swaps, and controls",
        layout: "image-right",
      },
    ],
    includeDemoVideo: true,
    ogImage: "/og-volume-bot.png",
  },
  "pair-bot": {
    key: "pair-bot",
    toolName: "Solana Pair Bot",
    toolPath: "/pair-bot",
    learnMorePath: "/pair-bot/learn-more",
    category: "Growth Automation",
    primaryKeyword: "solana pair bot",
    seoTitle: "Solana Pair Bot Guide | Two-Token Volume",
    pageHeading: "Solana Pair Bot Guide",
    metaDescription:
      "IDX pair bot guide: paste two Solana token mints, pick a pool for each, and trade your token with a second token in one bot.",
    description:
      "The IDX pair bot trades your SPL token with a second token in one bot, so one campaign adds on-chain volume to both charts.",
    summary:
      "Use Pair when you want volume on two tokens at once. Paste both mints, pick pools, fund about 0.04 SOL per hour, and monitor both markets from the dashboard.",
    primaryUseCase:
      "Use the pair bot when both tokens already trade on supported pools and you want to trade your token with a second token in one bot instead of quoting IDX.",
    audience: [
      "Founders who want chart activity on two related tokens",
      "Teams pairing a new mint with an existing token",
      "Operators who want V4 Eco cadence without an IDX quote",
    ],
    steps: [
      "Open the pair bot page and connect your Solana wallet.",
      "Paste your token mint and confirm the symbol.",
      "Select the Pair engine, paste the Token 2 address, and confirm that token.",
      "Pick a pool for each token, recharge in SOL, then start, stop, or refund unused gas from the dashboard.",
    ],
    faq: [
      {
        q: "What does the pair bot trade?",
        a: "It trades your token with a second token in one bot. It does not quote IDX. Both tokens need a supported pool before you launch.",
      },
      {
        q: "How fast does it trade and what does it cost?",
        a: "About every 7–10 minutes at ~0.04 SOL per hour (~0.96 SOL per day). Listed output is about 80 SOL of volume and 125+ makers per token. The recharge screen shows the minimum before you send SOL.",
      },
      {
        q: "Does a pair bot guarantee DexScreener trending?",
        a: "No. It increases on-chain trade count on both tokens. Rankings still depend on liquidity and market demand.",
      },
      {
        q: "Can I stop the pair bot?",
        a: "Yes. Stop it from the dashboard and refund unused gas under the same rules as other IDX volume bots.",
      },
    ],
    intentSections: [
      {
        title: "When a pair bot fits",
        body: "Use Pair when you already have two tokens with tradable pools and you want one bot to print activity on both, using the second token as the quote. It follows the light V4 Eco cadence, so it suits steady campaigns more than an aggressive V12 push.",
      },
      {
        title: "What to check before funding",
        body: "Confirm both mint addresses, pick the pool you actually want for each token, and set a SOL budget you can leave running. Assign someone to watch both charts and the gas meter, then stop and refund unused gas when the campaign ends.",
      },
    ],
    riskNotes: [
      "Pair automation does not guarantee price, rankings, or listings on either token.",
      "Wrong mint or the wrong pool spends SOL on a market you did not mean to trade.",
      "Both tokens need real liquidity. Thin pools increase slippage.",
      "Stop the bot and refund unused gas from the dashboard when you pause.",
    ],
    decisionChecklist: [
      "Both token mints are verified and already trade.",
      "A pool is selected for token 1 and token 2.",
      "SOL budget covers the ~0.04 SOL/hour pace you plan to run.",
      "Someone is assigned to monitor both markets and the dashboard.",
    ],
    related: ["volume-bot", "liquidity-pool-creator", "token-hub"],
    extraRelatedLinks: [
      { href: "/pair-bot", label: "Open the Solana pair bot" },
      { href: "/volume-bot", label: "Compare other volume engines" },
      { href: "/volume-bot/learn-more", label: "Read the Solana volume bot guide" },
      { href: "/liquidity-pool-creator", label: "Create a pool before you launch" },
    ],
    heroCtaLabel: "Open pair bot",
    faqCta: {
      label: "Start pair bot setup",
      href: "/pair-bot",
    },
    ogImage: "/og-volume-bot.png",
  },
  "telegram-bot": {
    key: "telegram-bot",
    toolName: "IDX Telegram Volume Bot",
    toolPath: IDX_TELEGRAM_BOT_URL,
    externalTool: true,
    learnMorePath: "/telegram-bot/learn-more",
    category: "Telegram Mini App",
    accentTheme: "cyan",
    primaryKeyword: "telegram solana volume bot",
    seoTitle:
      "Telegram Solana Volume Bot — IDX Mini App | Not a Chat Bot",
    metaDescription:
      "Run Solana volume bots in Telegram: paste a mint, pick a pool and engine, and manage bots from your phone. Open @IDX_AutoBot.",
    seoKeywords: [
      "telegram solana volume bot",
      "solana volume bot telegram",
      "idx autobot telegram",
      "telegram mini app crypto bot",
      "solana volume bot mobile",
      "dexscreener volume bot telegram",
      "idx volume bot telegram",
      "telegram trading bot solana",
    ],
    description:
      "IDX Telegram Volume Bot is a full Telegram Mini App for Solana — not a text-only command bot. Paste an SPL mint, auto-detect token metadata and liquidity pools, pick V4–V12 or Mini Buy/Sell engines, launch bots, and manage your dashboard from Telegram on mobile or desktop.",
    summary:
      "Open @IDX_AutoBot in Telegram for a real mini app: mint → pool → engine → launch. Same IDX volume engines as the web dashboard, live pool stats, bot cards with recent trades, and built-in AI support — without memorizing slash commands.",
    audience: [
      "Solana founders who want to launch or monitor volume bots from their phone",
      "Growth teams that live in Telegram and need a visual bot dashboard, not chat commands",
      "Traders managing V12, V10, V8, V6, or Mini Buy/Sell campaigns while away from desktop",
      "Projects pairing Telegram community ops with on-chain volume automation",
    ],
    steps: [
      "Paste your SPL token address or pool — the Mini App auto-detects the token, symbol, and eligible pools with live liquidity and volume.",
      "Select your engine — V12, V10, V8, V6, V4, Mini Buy, or Mini Buy/Sell — and compare volume targets, trade frequency, and hourly SOL cost on each card.",
      "Recharge in SOL, launch the bot, then come back anytime to monitor gas, live swaps, and status from your phone.",
    ],
    faq: [
      {
        q: "Is IDX AutoBot just a Telegram chat bot with commands?",
        a: "No. IDX AutoBot opens a full Telegram Mini App with a visual UI — create flows, engine cards, pool pickers, dashboards, and live trade feeds. You tap buttons and forms instead of typing slash commands or parsing bot replies.",
      },
      {
        q: "Does the Telegram bot use the same engines as the web volume bot?",
        a: "Yes. V12, V10, V4, V4 Eco, V6, V8, Mini Buy, and Mini Buy/Sell are available in the Mini App with the same on-chain automation as tools.idxsolana.io. Strong ranking setups often pair V12 with Mini Buy/Sell on the same token.",
      },
      {
        q: "Which Solana pools work in the Telegram Mini App?",
        a: "Supported routes include Pump.fun, Raydium, Orca, Meteora, IDX Launchlab, and related pools. Paste your mint and the app lists eligible pools with liquidity and volume so you pick the right pair before launch.",
      },
      {
        q: "Can I manage bots from my phone?",
        a: "Yes. The Mini App dashboard shows bot cards with gas level, engine type, pair, recent buys and sells, and controls to recharge, pause, or refund unused gas — optimized for Telegram on mobile.",
      },
      {
        q: "How do I open the bot?",
        a: "Go to t.me/IDX_AutoBot in Telegram (mobile or desktop), tap Start, then open the Mini App from the menu or inline button. No separate app install — it runs inside Telegram.",
      },
      {
        q: "Is there AI support inside Telegram?",
        a: "Yes. A support button in the Mini App opens IDX AI help for volume bot questions, engine choice, and troubleshooting — without leaving Telegram.",
      },
      {
        q: "Does a volume bot guarantee DexScreener trending?",
        a: "No. Volume bots increase on-chain activity and transaction count, which teams combine with marketing for discovery. Rankings depend on market demand, competition, and broader visibility — not automation alone.",
      },
    ],
    intentSections: [
      {
        title: "Not a boring chat bot",
        body: "Most Telegram crypto bots are text menus and opaque replies. IDX AutoBot is a native Mini App — rich UI, pool detection, engine comparison cards with live stats, and a dashboard you would expect from a web product, running inside Telegram.",
      },
      {
        title: "Mobile-first volume automation",
        body: "Launch windows do not wait for you to reach a laptop. Paste a mint on your phone, pick V12, V10, V8, or Mini Buy/Sell, fund in SOL, and watch recent trades stream on the bot card while you manage community chat in the same app.",
      },
      {
        title: "Transparent engine choice",
        body: "Each engine card shows daily volume targets, maker counts, trade frequency, and hourly SOL cost before you commit. Compare V12 top rankings vs V10 continuous high tx vs V8 aggressive flow vs V6 trending vs V4 organic vs Mini Buy/Sell micro swaps side by side.",
      },
      {
        title: "Same IDX stack as the web",
        body: "Telegram is a front door — not a stripped-down clone. Bots, gas, refunds, and on-chain execution use the same IDX infrastructure as the browser dashboard, so you can switch between phone and desktop without reconfiguring.",
      },
    ],
    engineGuide: {
      title: "Engines in the Telegram Mini App",
      intro:
        "Pick the engine that matches your launch stage and budget. The Mini App shows live activity previews on each card so you know what you are buying before Launch bot.",
      engines: [
        {
          id: "V12",
          label: "V12 — top ranking",
          body: "~15000 SOL/day, 19000+ makers/day, trades instantly. Best for aggressive ranking pushes (~4 SOL/hr); pairs with Mini Buy/Sell.",
        },
        {
          id: "V10",
          label: "V10 — continuous high tx",
          body: "Continuous unique-maker flow (~1 SOL/hr, ~3000 SOL/day, unique high tx). First charge 24 SOL. Best when you want a busy tape without V12 spend.",
        },
        {
          id: "V8",
          label: "V8 — fast volume + makers",
          body: "3000+ SOL/day volume, 4000+ makers/day, trades every 15–30 seconds. Best for launch windows and post-graduation visibility pushes (~0.5 SOL/hr).",
        },
        {
          id: "V6",
          label: "V6 — trending momentum",
          body: "~350 SOL/day, 700+ makers/day, trades every 1–5 minutes. Steady DexScreener activity without V8 hourly spend (~0.15 SOL/hr).",
        },
        {
          id: "V4",
          label: "V4 — low-cost organic flow",
          body: "~100 SOL/day, 125+ makers/day, trades every 6–9 minutes. Budget-friendly prints for early curve or thin liquidity (~0.045 SOL/hr).",
        },
        {
          id: "MiniBuySell",
          label: "Mini Buy/Sell — micro swap density",
          body: "Alternating micro buys and sells — ~30 swaps/min. Pairs with V12 or V8 for layered chart activity and higher transaction count (~0.30 SOL/hr).",
        },
        {
          id: "MiniBuy",
          label: "Mini Buy — bid-side depth",
          body: "Buy-side only — rapid micro purchases (~0.1 SOL/hr, ~10 swaps/min) that stack bid depth and tx count without alternating sells. Pair with V12 or V8 when you want buys plus two-sided flow.",
        },
      ],
    },
    promoCallout: {
      title: "Strong ranking setup: V12 + Mini Buy/Sell",
      body: "Run V12 for volume and makers, plus Mini Buy/Sell for dense micro trade activity on the same token. The web dashboard tip and volume bot guide explain why teams stack engines this way.",
      href: "/volume-bot/learn-more",
      ctaLabel: "Volume bot strategy guide",
    },
    riskNotes: [
      "Volume automation does not guarantee price action, bonding curve completion, or exchange listings.",
      "Set SOL recharge limits you are comfortable losing to gas and slippage before scaling.",
      "Verify mint address and pool support in the Mini App before funding.",
      "Pause bots and refund unused gas from the dashboard when a campaign ends or ROI is off target.",
    ],
    decisionChecklist: [
      "Token mint is verified and trades on a supported pool.",
      "Correct pool is selected with adequate liquidity for your engine tier.",
      "Engine choice matches budget (V4/V6 vs V8/V10/V12/Mini Buy/Sell).",
      "Landing page, socials, and narrative are ready before visibility spikes.",
      "Someone is assigned to monitor gas and bot health from Telegram or web.",
    ],
    related: ["volume-bot", "holders-maker", "reaction-booster", "token-hub"],
    extraRelatedLinks: [
      { href: "/volume-bot", label: "Solana Volume Bot (web)" },
      { href: "/idx-volume-bot", label: "IDX Launchlab volume bot" },
      { href: "/pumpfun-volume-bot", label: "Pump.fun volume bot" },
    ],
    faqCta: {
      label: "Open @IDX_AutoBot in Telegram",
      href: IDX_TELEGRAM_BOT_URL,
    },
    heroIllustration: {
      src: "/telegram-bot/learn-more/hero-cyberpunk.png",
      videoSrc: "/telegram-bot/splash/idx-loading.mp4",
      poster: "/telegram-bot/learn-more/hero-cyberpunk.png",
      uploadDate: "2026-07-27T01:58:40+00:00",
      alt: "IDX Telegram volume bot — cyberpunk robot driving a sports car, representing automated Solana trading",
      width: 480,
      height: 270,
    },
    visualBlocks: [
      {
        id: "create-flow",
        title: "Paste mint, pick pool — done in taps",
        description:
          "Enter an SPL mint or pool address and the Mini App resolves token name, symbol, and logo. Select from detected pools with live liquidity and volume — Raydium / SOL, Pump.fun routes, and more — before you choose an engine.",
        imageSrc: "/telegram-bot/learn-more/create-pool-selected.png",
        imageAlt:
          "IDX Telegram Mini App create bot screen — token mint detected with Raydium pool selection and live liquidity stats",
        layout: "image-right",
      },
      {
        id: "engine-picker",
        title: "Compare engines with live trade previews",
        description:
          "V12, V10, V8, V6, V4, Mini Buy, and Mini Buy/Sell cards show volume targets, maker counts, trade frequency, hourly SOL cost, and recent buy/sell activity — so you pick the right strategy without guessing from a text menu.",
        imageSrc: "/telegram-bot/learn-more/engine-picker.png",
        imageAlt:
          "IDX Telegram Mini App engine picker — V12, V10, V8, V6, V4, Mini Buy, and Mini Buy/Sell cards with stats and live trades",
        layout: "image-left",
        imageSize: "large",
      },
      {
        id: "dashboard-mobile",
        title: "Dashboard and controls inside Telegram",
        description:
          "Jump to Dashboard from any screen. Bot cards show gas level, engine type, pair, recent swaps, and actions to recharge or stop — the same operational view as the web product, optimized for Telegram.",
        imageSrc: "/telegram-bot/learn-more/telegram-bot-dashboard-card.png",
        imageAlt:
          "IDX Telegram Mini App bot card — Mini Buy/Sell with live swaps, gas level, and recharge controls",
        layout: "image-right",
      },
    ],
    imageStrip: [
      {
        id: "bot-profile",
        src: "/telegram-bot/learn-more/bot-profile.png",
        alt: "IDX Solana Volume Bot Telegram profile — 50,000+ monthly users",
        caption: "Trusted by tens of thousands of Telegram users every month.",
      },
      {
        id: "hero-alt",
        src: "/telegram-bot/learn-more/telegram-bot-dashboard-card.png",
        alt: "IDX Telegram Mini App bot card — Mini Buy/Sell on Raydium with live swaps, gas level, and recharge controls",
        caption: "Manage bots from Telegram — live trades, gas, pause, and recharge on one card.",
      },
      {
        id: "create-empty",
        src: "/telegram-bot/learn-more/create-pool-selected.png",
        alt: "Telegram Mini App create bot — SPL mint with Raydium pool selected and live liquidity stats",
        caption: "Paste mint, confirm token, pick pool — then continue to engine selection.",
      },
    ],
    ogImage: "/telegram-bot/learn-more/og-telegram-volume-bot.png",
    ogImageAlt:
      "IDX Telegram Solana volume bot — engine picker with V12, V10, V8, V6, V4, and Mini Buy/Sell",
  },
  "holders-maker": {
    key: "holders-maker",
    toolName: "Holders Maker",
    toolPath: "/holders-maker",
    learnMorePath: "/holders-maker/learn-more",
    category: "Growth Automation",
    primaryKeyword: "solana holders maker",
    seoTitle: "Solana Holders Maker Guide | Increase Token Holders",
    metaDescription:
      "Solana Holders Maker guide: distribute SPL tokens to managed wallets, grow holder count, plan SOL budgets, and pair with volume tools.",
    seoKeywords: [
      "solana holders maker",
      "increase token holders solana",
      "solana holder bot",
      "spl holder distribution",
      "grow token holders",
      "holders maker guide",
    ],
    description:
      "Solana Holders Maker distributes SPL tokens to managed wallets so teams can increase token holder count and improve holder diversity without manual airdrop spreadsheets.",
    summary:
      "This guide explains how to plan, fund, and run Solana Holders Maker campaigns — paste your mint, create the holder bot, fund network fees in SOL, and review holder growth from your IDX dashboard.",
    primaryUseCase:
      "Use Holders Maker when your SPL mint is live and you want managed wallet distribution to grow holder count — without building airdrop spreadsheets or hand-sending transfers.",
    audience: [
      "Token founders who need visible holder growth after launch",
      "Community leads improving wallet distribution and trust signals",
      "Growth teams pairing holder campaigns with volume or reaction tools",
      "Projects moving from a concentrated wallet cluster to broader on-chain distribution",
    ],
    steps: [
      "Sign in to IDX, open Solana Holders Maker, and paste your valid SPL token mint address for validation.",
      "Click Create Holder Bot to provision secure distribution wallets managed by IDX automation.",
      "Fund the bot with SOL for network fees, confirm distribution parameters, and start the holder growth run.",
      "Monitor holder count and spend from your dashboard, then document results and pair next steps with Token Hub, volume, or liquidity tools.",
    ],
    faq: [
      {
        q: "What does Solana Holders Maker do?",
        a: "IDX Holders Maker distributes SPL tokens to genuine wallets managed by the bot so you can increase holder count without external wallet lists. It does not scan the chain for existing holders — it distributes to wallets provisioned for your campaign.",
      },
      {
        q: "Why does holder distribution matter?",
        a: "Broader wallet distribution improves perceived community size and credibility on explorers and analytics. It supports trust signals alongside liquidity and volume — not as a substitute for organic community building.",
      },
      {
        q: "What do I need to start, and is coding required?",
        a: "You need a valid SPL mint (32–44 base58 characters). No coding is required — validate the mint, create the holder bot, fund SOL for fees, and monitor from the IDX dashboard.",
      },
      {
        q: "Can I combine Holders Maker with other IDX tools?",
        a: "Yes. Teams often pair it with token creation, liquidity setup, and the Solana volume bot for holder growth plus chart activity. Define KPI and budget first, and assign someone to monitor bot health daily.",
      },
    ],
    intentSections: [
      {
        title: "Holder growth planning on Solana",
        body: "Solana Holders Maker fits campaigns where you need predictable holder growth with measurable steps and budget control. Use it when your token is live, the mint is verified, and you want managed wallet distribution instead of one-off transfers. Plan target holder count, pacing, and a monitoring owner upfront so you validate small runs before scaling SOL fees. Visible holder diversity also supports launch credibility when liquidity alone is not enough.",
      },
      {
        title: "Execution quality and the IDX launch stack",
        body: "Fund network fees in SOL, confirm distribution wallets provision correctly, and review holder growth on the dashboard before increasing budget. Holders Maker works best after token creation and liquidity; combine with Token Hub for mint verification, Reaction Booster for engagement, or a volume bot when you want holders and active trading together. Keep post-campaign analytics tied to one canonical mint context.",
      },
    ],
    engineGuide: {
      title: "How Solana Holders Maker works",
      intro:
        "The holder bot provisions managed wallets, funds Solana network fees, and distributes SPL tokens from your campaign config. Review both stages before you scale.",
      engines: [
        {
          id: "mint-and-bot",
          label: "Validate mint and provision the bot",
          body: "Paste your SPL mint for format and context checks, then create the Holder Bot so IDX provisions secure distribution wallets without manual key management.",
        },
        {
          id: "fund-and-review",
          label: "Fund, distribute, and review",
          body: "Transfer SOL for fees, let automation distribute SPL to managed wallets, compare outcomes to your KPI, and scale budget only when the validation run looks right.",
        },
      ],
    },
    riskNotes: [
      "Holder growth automation does not replace authentic community building or compliant marketing disclosures.",
      "Start with a small validation run before scaling SOL spend on distribution fees.",
      "Verify mint address and token metadata before funding — incorrect mints waste fees and confuse reporting.",
      "Monitor bot health and remaining SOL daily; assign an owner for post-run review.",
      "Holder count alone is not investment advice or a guarantee of price performance or DexScreener rankings.",
    ],
    decisionChecklist: [
      "Holder growth objective and KPI are defined before launch.",
      "SPL mint address and token metadata are verified on-chain.",
      "Budget, pacing limits, and SOL fee buffer are approved.",
      "Monitoring owner is assigned for dashboard and bot health checks.",
      "Post-run review criteria and community messaging are documented.",
    ],
    related: [],
    extraRelatedLinks: [
      {
        href: "/volume-bot",
        label: "Pair holder growth with a Solana volume bot",
      },
      {
        href: "/reaction-booster",
        label: "Run Reaction Booster for Telegram launch engagement",
      },
      {
        href: "/token-hub",
        label: "Verify mint and holder context in Token Hub",
      },
      {
        href: "/token-creator",
        label: "Create your SPL token before holder distribution",
      },
      {
        href: "/liquidity-pool-creator",
        label: "Add Raydium liquidity before scaling holder campaigns",
      },
    ],
    heroCtaLabel: "Start holder growth campaign",
    faqCta: {
      label: "Open the Holders Maker tool",
    },
    ogImage:
      "https://res.cloudinary.com/dvrevmrbp/video/upload/so_0,f_jpg,q_auto,w_1200/v1780566823/signal-2026-06-04-145952_r8cbkb.jpg",
    ogImageAlt: "Solana Holders Maker — increase token holders guide",
  },
  "smart-sell": {
    key: "smart-sell",
    toolName: "Smart Sell",
    toolPath: "/smart-sell",
    learnMorePath: "/smart-sell/learn-more",
    category: "Take your profit out",
    primaryKeyword: "solana smart sell",
    seoTitle: "Smart Sell Guide | Take Profit Into Solana Buys",
    pageHeading: "Smart Sell Guide — sell when someone else buys",
    metaDescription:
      "Watch your Solana pool and sell a chosen percent of each real buy above your target market cap. Fund a deposit with tokens only — no SOL required. Skip team wallets. No dump from one wallet.",
    seoKeywords: [
      "solana smart sell",
      "sell into buys",
      "token sell bot",
      "solana take profit bot",
      "target market cap sell",
      "skip wallets token bot",
    ],
    ogImage: "/smart-sell/smart-sell-buy-v2.jpg",
    description:
      "Smart Sell watches the chart you pick. When someone else buys your token with enough SOL — and market cap is at or above your target — it sells a slice of tokens (25% of that buy) from fresh wallets so you take profit without one giant dump candle.",
    summary:
      "This guide explains Smart Sell in plain language: pick a mint and pool, set a target market cap, choose skip wallets, fund the deposit bag with tokens, then read the bot card — how much sold, how much is left, and how many wallets you chose.",
    primaryUseCase:
      "Use Smart Sell when you want to take profit into real demand without dumping your whole bag from one wallet on the chart.",
    audience: [
      "Token teams who want gradual exits into organic buys",
      "Founders who already run volume tools and need a reactive sell path",
      "Anyone who wants a simple “someone buys → we sell a slice” rule",
      "Operators who need clear sold / bag / wallet counts on one card",
    ],
    steps: [
      "Open Smart Sell and enter the token address — paste the mint or pick from a connected wallet. The pool selector appears once a token with pools is found.",
      "Set your target market cap in USD — below it the bot waits; at or above it, qualifying buys can trigger a sell.",
      "Set sell percentage — how much of each qualifying buy to sell into (for example 25% of a 1 SOL buy aims at about 0.25 SOL of tokens), not a percent of your bag.",
      "Set max sell per trade — the most of your remaining bag one qualifying buy can take (default 10%, range 1–100%). Sell % sizes against the buy; this cap is against the bag.",
      "Optionally add skip wallets (team or bots) so those buys never trigger a sell.",
      "Create the bot, copy the deposit address, send tokens (no SOL deposit required), start when ready, and use Refund later for sold SOL or a full stop-and-refund.",
    ],
    faq: [
      {
        q: "What does Smart Sell do, in one sentence?",
        a: "When a real buyer spends SOL on your chart and market cap is high enough, Smart Sell sells a chosen percent of that buy size in tokens from your deposit bag — using fresh sell wallets — and sends SOL back to you.",
      },
      {
        q: "Does Smart Sell use a smart contract?",
        a: "No. You get a deposit address to fund. The bot watches buys with Helius webhooks plus a light poll fallback, then sells through managed pool wallets and returns SOL to the deposit wallet. Withdraw with Refund (sold SOL, or stop and fully refund).",
      },
      {
        q: "What buys are ignored?",
        a: "Buys under 0.1 SOL, buys from wallets you chose to skip, and any buy while live market cap is still below your target USD number.",
      },
      {
        q: "What if the market cap is already above my target when I start?",
        a: "It does not wait for market cap to drop. The target is a fixed USD number (for example 80% of the pool’s market cap when you set it). While live market cap stays at or above that number, the gate is open — any qualifying buy (a real mint buy, buyer not on your skip list, at least 0.1 SOL) can trigger a sell of a slice of that buy, capped at 10% of the bag per sell.",
      },
      {
        q: "What will I see on the bot card?",
        a: "Three clear groups: how much it sold (tokens, SOL received, sell count), how much wallets have left (tokens plus any sold SOL in the deposit bag), and how many wallets you chose (skip wallet count, unique sell wallets used, unique buyers sold into).",
      },
      {
        q: "What does “a chosen percent of the buy” mean?",
        a: "If someone buys with 1 SOL and your setting is 25%, Smart Sell aims to sell tokens worth about 0.25 SOL of that demand — not 25% of your entire bag. A per-sell cap also stops one reaction from emptying the deposit.",
      },
      {
        q: "What is max sell per trade?",
        a: "It caps how much of your token bag is sold on one qualifying buy, no matter how large the buy is. That protects your exit price from one oversized sale. Default is 10%. You can set 1–100% when you create the bot or later in settings.",
      },
    ],
    intentSections: [
      {
        title: "Take profit into demand, not against it",
        body: "Smart Sell is for teams who want exits that ride real buys. You set a market-cap finish line, fund a token bag, and let automation sell small slices when outsiders buy — so the chart does not show one wallet dumping everything at once.",
      },
      {
        title: "Read the scoreboard like a kid could",
        body: "The bot card is meant to answer three questions instantly: How much did we sell? How much is still in the bag? How many wallets did we choose or use? History lines spell out each buyer spend and the sell that followed in plain words.",
      },
      {
        title: "Max sell per trade",
        body: "Sell % sizes the slice against the incoming buy (for example 25% of a 1 SOL buy aims at about 0.25 SOL of tokens). Max sell per trade caps that slice against your remaining bag — default 10%, range 1–100%, set on create or in settings — so one large buy cannot dump the exit. Buys under 0.1 SOL are ignored first; they never reach either limit.",
      },
    ],
    engineGuide: {
      title: "How Smart Sell thinks",
      intro:
        "Four checks before every sell. If any check fails, the bot skips and moves on — nothing magic, just rules.",
      engines: [
        {
          id: "watch-pool",
          label: "Watch the pool you picked",
          body: "Only buys on your selected chart/pool count. Tiny noise under 0.1 SOL is ignored.",
        },
        {
          id: "mcap-gate",
          label: "Check your market-cap gate",
          body: "If live mcap is under your USD target, Smart Sell waits. At or above the target, sells can fire.",
        },
        {
          id: "size-slice",
          label: "Sell a chosen percent of the buy (25–90%)",
          body: "Sizing follows the buyer’s SOL amount, then a safety cap so one sell cannot drain the whole bag.",
        },
        {
          id: "fresh-wallets",
          label: "Use fresh sell wallets",
          body: "Tokens move from your deposit bag through unique pool wallets for the swap, then SOL comes back to the deposit.",
        },
      ],
    },
    riskNotes: [
      "Smart Sell does not guarantee profit, price support, or chart rankings.",
      "Fund only the shown deposit address — private keys are not shown in the UI; withdraw with Refund.",
      "Fund tokens only — swaps are gasless-first, so no SOL deposit is required.",
      "Add your own team and volume bots as skip wallets so you do not sell into yourself.",
      "Start with a small token bag to validate behavior before depositing a large inventory.",
    ],
    decisionChecklist: [
      "Mint and pool (chart) are verified.",
      "Target market cap USD matches your exit plan.",
      "Skip wallets for team/bots are listed.",
      "Deposit address funded with tokens (no SOL deposit required).",
      "Someone will watch sold / bag / wallet stats on the bot card.",
    ],
    related: ["volume-bot", "holders-maker", "reaction-booster"],
    extraRelatedLinks: [
      {
        href: "/volume-bot",
        label: "Pair exits with a Solana volume bot",
      },
      {
        href: "/token-hub",
        label: "Verify mint context in Token Hub",
      },
      {
        href: "/smart-sell",
        label: "Create a Smart Sell bot",
      },
    ],
    heroCtaLabel: "Open Smart Sell",
    faqCta: {
      label: "Create a Smart Sell bot",
    },
    heroIllustration: {
      src: "/volume_bot.png",
      alt: "IDX Autobot — Smart Sell",
      width: 240,
      height: 390,
    },
    visualBlocks: [
      {
        id: "hero",
        title: "Sell when someone else buys",
        description:
          "When a real buyer prints on your chart, Smart Sell takes a thin slice from your deposit — not a dump from one wallet.",
        imageSrc: "/smart-sell/smart-sell-hero.jpg",
        imageAlt:
          "IDX Autobot watching a Solana buy print and holding a 25% token slice",
      },
      {
        id: "intent-demand",
        title: "Take profit into demand, not against it",
        description:
          "Exits ride real buys. You set a market-cap finish line, fund a token bag, and sell small slices when outsiders buy.",
        imageSrc: "/smart-sell/smart-sell-buy-v2.jpg",
        imageAlt: "Solana chart showing an external buy that can trigger Smart Sell",
      },
      {
        id: "intent-scoreboard",
        title: "Read the scoreboard like a kid could",
        description:
          "The bot card answers three questions: How much did we sell? How much is still in the bag? How many wallets did we use?",
        imageSrc: "/smart-sell/smart-sell-scoreboard.jpg",
        imageAlt: "Smart Sell bot card showing sold totals, bag left, and wallets used",
      },
      {
        id: "engine",
        title: "How Smart Sell thinks",
        description:
          "Four checks before every sell: watch the pool, pass the market-cap gate, size a percent slice, use a fresh wallet.",
        imageSrc: "/smart-sell/smart-sell-engine-strip.jpg",
        imageAlt:
          "Four-panel Smart Sell checks — watch pool, market cap gate, percent slice, fresh wallets",
      },
      {
        id: "step-1",
        title: "Step 1 — enter the token",
        description:
          "Paste the mint or pick from a connected wallet. The pool selector appears once a token with pools is found.",
        imageSrc: "/smart-sell/smart-sell-step1.jpg",
        imageAlt: "Smart Sell token row with mint field, wallet picker, and pool selector",
      },
      {
        id: "step-3",
        title: "Step 3 — skip wallets",
        description:
          "Optionally add skip wallets (team or bots) so those buys never trigger a sell.",
        imageSrc: "/smart-sell/smart-sell-step3.jpg",
        imageAlt: "Skip-wallets list with addresses marked ignored",
      },
      {
        id: "step-4",
        title: "Step 4 — fund and refund",
        description:
          "Copy the deposit address, send tokens (no SOL deposit required), then Refund sold SOL or stop and fully refund.",
        imageSrc: "/smart-sell/smart-sell-wallets-v3.jpg",
        imageAlt: "Smart Sell deposit receiving SOL after a sell",
      },
    ],
  },
  "smart-tools": {
    key: "smart-tools",
    toolName: "Smart Tools",
    toolPath: "/smart-tools",
    learnMorePath: "/smart-sell/learn-more",
    category: "Growth Automation",
    primaryKeyword: "solana smart sell",
    seoTitle: "Smart Tools | Smart Sell and Reactive Token Utilities",
    metaDescription:
      "IDX Smart Tools hub — start with Smart Sell to take profit into Solana buys without dumping the chart.",
    seoKeywords: [
      "solana smart tools",
      "solana smart sell",
      "sell into buys",
    ],
    description:
      "Smart Tools are reactive helpers for Solana token teams. The first tool is Smart Sell: sell a slice into qualifying external buys above your target market cap.",
    summary:
      "Open the hub to reach Smart Sell. Create a deposit bag, set target market cap and skip wallets, then track sold totals, leftover balance, and wallets on the bot card.",
    primaryUseCase:
      "Use Smart Tools when you want automation that reacts to live market activity instead of running a fixed volume schedule.",
    audience: [
      "Token teams taking profit gradually",
      "Projects that already run volume tools and need a reactive exit path",
    ],
    steps: [
      "Open Smart Tools and choose Smart Sell.",
      "Paste your mint, pick the chart, and set target market cap USD.",
      "Whitelist team wallets, create the bot, and fund tokens only.",
      "Start when ready and read sold / bag / wallet stats on the card.",
    ],
    faq: [
      {
        q: "What is the first Smart Tool?",
        a: "Smart Sell — it sells a fraction of each qualifying external buy from unique wallets when market cap is at or above your target.",
      },
      {
        q: "Where do I manage Smart Sell bots?",
        a: "Create from Smart Sell (or Create Bot → Smart Sell), then open the dashboard to Start, view History, and Refund sold SOL or fully refund.",
      },
    ],
    related: ["smart-sell", "volume-bot", "holders-maker"],
    heroCtaLabel: "Open Smart Tools",
    faqCta: {
      label: "Open Smart Sell",
      href: "/smart-sell",
    },
  },
  "reaction-booster": {
    key: "reaction-booster",
    toolName: "Reaction Booster",
    toolPath: "/reaction-booster",
    learnMorePath: "/reaction-booster/learn-more",
    category: "Community Growth",
    primaryKeyword: "token reaction booster",
    seoTitle: "DexScreener Reaction Booster Guide | Solana",
    pageHeading: "DexScreener Reaction Booster Guide",
    metaDescription:
      "DexScreener reaction booster for Solana: automate hearts and rockets, plan SOL tiers, and pair profile engagement with volume campaigns.",
    seoKeywords: [
      "dexscreener reaction booster",
      "solana token reactions",
      "boost dexscreener hearts",
      "token reaction bot solana",
      "dexscreener engagement booster",
      "reaction booster guide",
    ],
    description:
      "Reaction Booster for DexScreener distributes hearts, rockets, fire, and related profile reactions to your Solana token page so teams can lift social proof signals alongside on-chain launch activity.",
    summary:
      "This guide explains how to paste your DexScreener URL, create a Reaction Booster bot, fund SOL for reaction tiers, and monitor engagement quality without treating reactions as a substitute for organic community building.",
    primaryUseCase:
      "Use Reaction Booster when your token already trades on DexScreener and you want visible profile engagement during launch windows, relists, or post-liquidity visibility pushes.",
    audience: [
      "Solana token founders boosting DexScreener profile engagement during launch week",
      "Community managers pairing social posts with visible on-page reaction momentum",
      "Growth teams running coordinated campaigns with volume bots and holder tools",
      "Marketing leads who need structured reaction budgets instead of manual one-off clicks",
    ],
    steps: [
      "Open Reaction Booster on IDX and paste your token's DexScreener URL — the tool validates the pair page before bot creation.",
      "Click Create Reaction Booster Bot to provision managed wallets that will distribute reactions to your profile.",
      "Select a reaction tier or custom count, fund the campaign in SOL according to the pricing panel, and confirm distribution parameters.",
      "Wait for network sync, monitor delivery on DexScreener, then pair outcomes with volume, holders, or Community Signal next steps.",
    ],
    faq: [
      {
        q: "What does DexScreener Reaction Booster do?",
        a: "IDX Reaction Booster automates hearts, rockets, fire, and related DexScreener profile reactions on your Solana pair page. Paste the full DexScreener URL (not just a mint), create a booster bot, fund SOL, and managed wallets deliver reactions without manual clicking.",
      },
      {
        q: "How much SOL does a reaction campaign cost?",
        a: "Pricing scales by reaction count — for example, 50 reactions from 0.1 SOL, 100 from 0.2 SOL, 500 from 0.75 SOL, and 1000 from 1.5 SOL, with proportional scaling above 1000. The UI shows the exact amount before you fund.",
      },
      {
        q: "Can reactions replace organic growth or volume?",
        a: "No. Reactions support profile social proof but cannot replace community building, liquidity, or on-chain activity. Many teams run Reaction Booster beside Solana volume bots or Holders Maker in the same launch window — coordinate budgets and messaging.",
      },
      {
        q: "What should I monitor during a reaction campaign?",
        a: "Track delivery on DexScreener, bot status on IDX, SOL spend versus tier, and whether the pair URL stays current after pool migrations. Pause or adjust if the profile page changes.",
      },
    ],
    intentSections: [
      {
        title: "DexScreener social proof during launch windows",
        body: "Reaction Booster fits campaigns where your Solana token already appears on DexScreener and you want visible profile engagement during high-traffic launch windows. Traders often scan reaction counts alongside volume and liquidity. Automating hearts, rockets, and fire gives a more active first impression while your team runs announcements and liquidity coordination — treat reactions as one signal in a broader stack, not proof of project quality alone.",
      },
      {
        title: "Budgeting, pairing tools, and expectations",
        body: "IDX uses transparent SOL tiers so you can plan spend before funding. Start with a validation tier, confirm reactions land on the correct pair, then scale. Pair with volume bots for transaction count, Holders Maker for wallet distribution, or Community Signal for a share page. Reactions do not guarantee trending or price performance — keep the DexScreener URL current after pool moves and communicate outcomes honestly.",
      },
    ],
    engineGuide: {
      title: "How Reaction Booster works",
      intro:
        "Reaction Booster moves from DexScreener URL validation through funded, managed-wallet delivery. Review both stages before launch-day spend.",
      engines: [
        {
          id: "url-and-bot",
          label: "Validate URL and provision the bot",
          body: "Paste your DexScreener pair URL so IDX confirms the correct Solana profile, then create the Reaction Booster bot to spin up managed wallets that will submit reactions.",
        },
        {
          id: "fund-and-deliver",
          label: "Fund tier and deliver reactions",
          body: "Choose a reaction count tier, fund SOL from the pricing panel, and let the bot sync with DexScreener. Monitor delivery on the profile and bot status from your IDX dashboard.",
        },
      ],
    },
    riskNotes: [
      "Reaction automation does not guarantee DexScreener trending, exchange listings, or token price performance.",
      "Verify the DexScreener pair URL before funding — wrong URLs waste SOL and deliver reactions to the wrong profile.",
      "Start with a smaller reaction tier to validate delivery before scaling spend on launch day.",
      "Reactions support visibility but cannot replace transparent community communication and compliant marketing.",
      "If you migrate pools or rebrand, update your DexScreener URL and re-verify before starting a new campaign.",
    ],
    decisionChecklist: [
      "DexScreener pair URL is verified and matches your live Solana trading pool.",
      "Reaction tier and SOL budget are approved alongside other launch tool spend.",
      "Broader launch narrative, social posts, and liquidity are ready before engagement spikes.",
      "Dashboard owner is assigned to monitor bot delivery and DexScreener profile status.",
      "Community messaging explains reactions as support signals, not investment guarantees.",
    ],
    related: [],
    extraRelatedLinks: [
      {
        href: "/volume-bot",
        label: "Layer on-chain volume with DexScreener reactions",
      },
      {
        href: "/holders-maker",
        label: "Grow holder count alongside profile engagement",
      },
      {
        href: "/community",
        label: "Publish a share-ready Community Signal page",
      },
      {
        href: "/token-hub",
        label: "Verify mint context before reaction campaigns",
      },
      {
        href: "/pumpfun-volume-bot",
        label: "Boost Pump.fun pairs that feed DexScreener profiles",
      },
    ],
    heroCtaLabel: "Start DexScreener reaction campaign",
    faqCta: {
      label: "Open Reaction Booster tool",
    },
    faqHeading: "DexScreener Reaction Booster FAQ",
    ogImage: "/reaction_booster.png",
    ogImageAlt: "DexScreener reaction booster guide for Solana tokens",
  },
  "token-burn": {
    key: "token-burn",
    toolName: "Token Burn",
    toolPath: "/token-burn",
    learnMorePath: "/token-burn/learn-more",
    category: "Token Management",
    primaryKeyword: "solana token burn",
    description: "Learn when and how token burn is used in supply strategy and trust communication.",
    summary: "This guide explains burn mechanics, trade-offs, and execution steps.",
    audience: ["Token founders", "Treasury operators"],
    steps: ["Verify mint and authority.", "Confirm irreversible amount.", "Execute and publish proof."],
    faq: [{ q: "Is burning reversible?", a: "No. Burned supply is permanently removed." }],
    related: ["update-metadata", "token-hub", "revoke-mint-authority"],
    ogImage: "/token-burn-hero.jpeg",
  },
  "bulk-sender-solana": {
    key: "bulk-sender-solana",
    toolName: "Bulk Sender (Solana)",
    toolPath: "/bulk-sender/solana",
    learnMorePath: "/bulk-sender/solana/learn-more",
    category: "Distribution",
    primaryKeyword: "solana bulk sender",
    seoTitle: "Solana Bulk Sender Guide | SPL Airdrops & Multisend",
    pageHeading: "Solana Bulk Sender Guide — Airdrops & Multisend",
    metaDescription:
      "Solana bulk sender guide: distribute SOL or SPL to many wallets with CSV upload, batch validation, and optimized multisend — no coding required.",
    seoKeywords: [
      "solana bulk sender",
      "solana airdrop tool",
      "bulk send spl tokens",
      "solana multisender",
      "spl token airdrop",
      "mass send solana",
      "token distribution solana",
    ],
    description:
      "IDX Bulk Sender (Solana) distributes SOL or SPL tokens to many wallets in optimized batches — ideal for airdrops, presale allocations, staking rewards, team payments, and community reward campaigns without writing scripts.",
    summary:
      "This guide covers preparing recipient lists, validating mint and amounts, funding batches safely, executing multisend on Solana, and confirming receipts while avoiding common CSV and balance mistakes.",
    primaryUseCase:
      "Use Solana Bulk Sender when you need to pay or airdrop the same token to dozens or thousands of wallets faster and more reliably than sending transfers one by one from a wallet UI.",
    audience: [
      "Token teams running presale, whitelist, or community airdrop distributions",
      "Ops and treasury managers sending rewards or payroll in SOL or SPL",
      "Community managers distributing campaign prizes to many holders",
      "Founders scaling holder outreach after launch without custom scripts",
    ],
    steps: [
      "Connect your Solana wallet and select the SPL token or SOL you want to distribute from your wallet list.",
      "Paste recipient addresses with amounts (one per line) or upload a formatted CSV file — validate row count and decimal formatting before signing.",
      "Review total send amount, estimated fees, and batch count; confirm your wallet holds enough token balance and SOL for rent and transaction fees.",
      "Start multisend and approve each batch transaction from your wallet as IDX processes recipients in optimized on-chain batches.",
      "Verify successful transfers on Solana explorers, export or save your recipient list, and communicate completion links to your community.",
    ],
    faq: [
      {
        q: "What is Solana Bulk Sender?",
        a: "Solana Bulk Sender on IDX lets you send SOL or SPL tokens to many wallets in one workflow. Upload a CSV or paste address-and-amount lines, then execute optimized multisend batches instead of manual one-by-one transfers.",
      },
      {
        q: "Can I airdrop SPL tokens and SOL?",
        a: "Yes. Select an SPL token from your connected wallet or distribute native SOL. The tool validates decimals and formats amounts before you approve batch transactions.",
      },
      {
        q: "How should I format my recipient CSV?",
        a: "Use one recipient per line with wallet address and amount separated clearly (as shown in the tool example). Remove header rows, fix invalid addresses, and double-check decimal places match your token's decimals before uploading.",
      },
      {
        q: "Why use bulk sender instead of manual sends?",
        a: "Bulk sender saves hours on large distributions, reduces copy-paste errors, batches transactions efficiently, and gives a repeatable workflow for airdrops, rewards, and treasury payouts.",
      },
      {
        q: "How much SOL do I need besides the tokens sent?",
        a: "You need enough SOL for transaction fees and any associated account rent across all batches. Review the tool's balance checks before starting — insufficient SOL stops batches mid-run.",
      },
      {
        q: "Can Bulk Sender replace Holders Maker?",
        a: "No. Bulk Sender pays specific amounts to a list you provide. Holders Maker distributes small amounts to many new wallets to grow holder count. Teams often use both — Bulk Sender for known recipients, Holders Maker for holder growth campaigns.",
      },
      {
        q: "What should I verify after a multisend run?",
        a: "Spot-check recipient wallets on Solana explorers, confirm total sent matches your CSV, and archive the distribution file for accounting. Notify recipients with transaction links rather than screenshots alone.",
      },
    ],
    intentSections: [
      {
        title: "When Solana bulk send beats manual transfers",
        body: "Manual wallet sends work for a handful of recipients but break down quickly at scale. Solana Bulk Sender fits presale allocations, ambassador rewards, staking payouts, and post-launch airdrops where the same SPL token or SOL must reach hundreds or thousands of addresses with auditable amounts. IDX validates your list, splits work into batches, and keeps your connected wallet in control of every signed transaction — you get script-level throughput without maintaining custom code.",
      },
      {
        title: "Preparing recipient lists and CSV hygiene",
        body: "Most bulk send failures trace back to list formatting — wrong decimals, duplicate addresses, truncated base58 strings, or spreadsheet exports that inject commas. Export a clean CSV, test with a small batch first, and keep a master file for compliance. Align token decimals with your mint before pasting amounts so recipients receive the intended quantity rather than off-by-decimal errors that are painful to unwind publicly.",
      },
      {
        title: "Treasury safety and batch execution",
        body: "Bulk distributions move real treasury value. Confirm you are on the correct mint, use a dedicated distribution wallet when possible, and fund enough SOL for fees across all batches before clicking start. Approve transactions deliberately during each batch step rather than rushing during live announcements. Pause if balances or validation errors appear — continuing with a partial list creates support debt and community confusion.",
      },
      {
        title: "Post-distribution communication",
        body: "After batches confirm, share explorer links and summarize total recipients in your community channels. Pair large airdrops with Token Hub verification or Community Signal pages so new holders understand token context. If you also run holder growth or volume campaigns, sequence Bulk Sender first for known wallets, then layer growth tools once distribution receipts are verified.",
      },
    ],
    engineGuide: {
      title: "Bulk Sender workflow stages",
      intro:
        "Solana Bulk Sender moves through list prep, validation, batch signing, and receipt verification. Understanding each stage prevents wasted SOL on malformed CSVs or underfunded treasuries.",
      engines: [
        {
          id: "connect-token",
          label: "Connect wallet and select asset",
          body: "Connect Phantom, Solflare, or another Solana wallet. Choose SOL or an SPL token from your holdings as the asset to distribute.",
        },
        {
          id: "list-upload",
          label: "Paste or upload recipients",
          body: "Enter address-and-amount lines or upload CSV. The tool parses rows and surfaces validation errors before any on-chain action.",
        },
        {
          id: "balance-check",
          label: "Balance and fee review",
          body: "Confirm token balance covers total send amount and SOL covers fees across planned batches. Fix list errors before approving transactions.",
        },
        {
          id: "batch-send",
          label: "Multisend batches",
          body: "Approve each optimized batch from your wallet. IDX processes recipients in groups sized for reliable Solana confirmation.",
        },
      ],
    },
    riskNotes: [
      "Bulk sends are irreversible — verify mint, decimals, and recipient list before signing the first batch.",
      "Run a small test batch to a few known wallets before distributing to thousands of addresses.",
      "Insufficient SOL for fees can halt mid-campaign; fund the distribution wallet with buffer SOL.",
      "Keep private keys in your wallet only — IDX never custodies your seed phrase or unsigned treasury keys.",
      "Document distributions for your community; bulk airdrops without context can trigger support spam or scam accusations.",
    ],
    decisionChecklist: [
      "Correct SPL mint or SOL selected for the campaign.",
      "Recipient CSV validated for address format, decimals, and duplicate rows.",
      "Treasury wallet funded for total token amount plus SOL fee buffer.",
      "Test batch sent to internal wallets and verified on explorers.",
      "Community announcement draft ready with explorer links and support contacts.",
    ],
    related: [],
    extraRelatedLinks: [
      {
        href: "/holders-maker",
        label: "Grow holder count after targeted airdrops",
      },
      {
        href: "/token-hub",
        label: "Verify mint and share token context post-airdrop",
      },
      {
        href: "/token-creator",
        label: "Create the SPL token you plan to distribute",
      },
      {
        href: "/community",
        label: "Publish Community Signal for airdrop recipients",
      },
      {
        href: "/volume-bot",
        label: "Boost visibility after distribution campaigns",
      },
    ],
    heroCtaLabel: "Open Solana Bulk Sender",
    faqCta: {
      label: "Start SPL multisend batch",
    },
    faqHeading: "Solana Bulk Sender FAQ",
    ogImage: "/IDX_Bulk_sender_v1.png",
    ogImageAlt: "Solana bulk sender guide — SPL airdrops and multisend",
  },
  "update-metadata": {
    key: "update-metadata",
    toolName: "Update Token Metadata",
    toolPath: "/update-metadata",
    learnMorePath: "/update-metadata/learn-more",
    category: "Token Management",
    primaryKeyword: "update token metadata solana",
    seoTitle: "Update SPL Metadata on Solana | IDX Guide",
    pageHeading: "Update Solana Token Metadata Guide",
    metaDescription:
      "Update Solana token metadata: change name, symbol, image, description, tags, and socials on SPL and Token-2022 mints when you hold update authority.",
    seoKeywords: [
      "update token metadata solana",
      "solana metadata update",
      "spl token metadata editor",
      "token-2022 metadata update",
      "change token name symbol solana",
      "update authority solana token",
    ],
    description:
      "Update Token Metadata on IDX lets Solana teams revise SPL and Token-2022 metadata — name, symbol, image, description, tags, and social links — when the connected wallet holds update authority.",
    summary:
      "This guide walks through checking update authority, preparing branding assets, submitting on-chain metadata changes, and verifying explorer output so wallets, DEXs, and Community Signal pages show consistent token identity.",
    primaryUseCase:
      "Use Update Token Metadata after launch when you need to fix branding mistakes, refresh logos, add social links, or align on-chain metadata with a rebrand before scaling marketing.",
    audience: [
      "Token founders correcting name, symbol, or logo issues after initial mint",
      "Brand teams aligning on-chain metadata with website and social identity",
      "Community operators updating descriptions and social links before major campaigns",
      "Projects migrating from test metadata to production-ready SPL or Token-2022 profiles",
    ],
    steps: [
      "Connect the wallet that holds update authority for your SPL or Token-2022 mint and select the token from your wallet list.",
      "Review current metadata fields — name, symbol, image URI, description, tags, and social links — and prepare updated assets hosted on permanent storage.",
      "Edit fields within IDX limits (name, symbol, description length, and tag count), upload a new image if needed, and preview changes before signing.",
      "Submit the signed on-chain metadata update transaction and wait for confirmation on Solana.",
      "Verify the new metadata on Solana explorers, Jupiter, DexScreener, and Token Hub, then refresh Community Signal and marketing links.",
    ],
    faq: [
      {
        q: "Who can update Solana token metadata?",
        a: "Only the wallet holding update authority for your mint can submit metadata changes. IDX Update Token Metadata checks authority before you edit fields. If update authority was revoked or transferred, you cannot change metadata through this tool.",
      },
      {
        q: "Does Update Token Metadata work for SPL and Token-2022?",
        a: "Yes. IDX detects whether your mint uses the standard SPL Token program or Token-2022 and routes the correct metadata update flow. Extension-heavy Token-2022 configs may have additional constraints — verify compatibility before signing.",
      },
      {
        q: "Which fields can I change?",
        a: "You can update name, symbol, image, description, tags (up to three predefined categories), and social links including website, Twitter, Telegram, and Discord. Field length limits apply to keep metadata compatible with wallets and indexers.",
      },
      {
        q: "Why does metadata quality matter for Solana tokens?",
        a: "Clean, consistent metadata improves recognition in wallets, DEX aggregators, DexScreener, and search surfaces. Mismatched names, broken image URIs, or missing social links reduce trust and slow down community onboarding during launch windows.",
      },
      {
        q: "Will metadata changes appear instantly everywhere?",
        a: "On-chain metadata updates confirm immediately on Solana, but third-party indexers, Jupiter, and DexScreener may cache old data for minutes to hours. Re-verify on explorers and allow time for caches to refresh before announcing changes.",
      },
      {
        q: "Should I update metadata before revoking authorities?",
        a: "Most teams finalize metadata — name, symbol, image, description, and socials — before revoking mint or freeze authority and before heavy marketing spend. Changing metadata after authority revokes is fine as long as update authority is still held.",
      },
      {
        q: "How does metadata update connect to Token Hub and Community Signal?",
        a: "After updating metadata on-chain, re-verify your mint in Token Hub and refresh Community Signal pages so public-facing copy and hub links reflect the new branding. This keeps support macros and social posts aligned with explorer output.",
      },
    ],
    intentSections: [
      {
        title: "Metadata as your on-chain brand layer",
        body: "Solana token metadata is the first identity layer traders see in wallets, explorers, and DEX interfaces. Update Token Metadata on IDX gives teams a no-code path to revise that identity when launch plans evolve — fixing typos in symbol fields, swapping placeholder art for production logos, or adding social links that were missing on day one. Because metadata lives on-chain, every change requires wallet-signed authority and propagates to indexers over time. Plan updates deliberately rather than iterating blindly during peak traffic.",
      },
      {
        title: "When to update versus when to redeploy",
        body: "Metadata updates suit corrections and refreshes on an existing mint. They do not change total supply, mint authority state, or pool addresses. If you need a wholly new token with different economics, create a new SPL mint instead. For most live projects, updating metadata is the right move for rebrands, logo upgrades, and social link additions while preserving holder balances and liquidity pool continuity.",
      },
      {
        title: "Preparing assets and social links",
        body: "Host token images on durable URIs before submitting updates — broken image links degrade trust across Jupiter, Phantom, and DexScreener. Align description copy with your website and Community Signal narrative so users do not see conflicting value propositions. IDX supports predefined tags like Meme, Airdrop, and Tokenization to help categorization on supported surfaces. Review all fields in the preview step before signing because on-chain updates consume SOL fees even when indexers lag.",
      },
      {
        title: "Post-update verification workflow",
        body: "After confirmation, verify metadata on Solana explorers and re-check Token Hub output. Notify your community with explorer links rather than screenshots alone. If you run volume bots, reaction campaigns, or holder tools, confirm those dashboards still reference the correct mint and that DexScreener shows updated symbols. Schedule a follow-up check 24 hours later when major indexers typically reflect fresh URIs.",
      },
    ],
    engineGuide: {
      title: "Update Token Metadata workflow",
      intro:
        "Metadata updates move through authority verification, field editing, on-chain submission, and indexer refresh. Understanding each stage prevents signing transactions with incomplete assets or wrong wallet authority.",
      engines: [
        {
          id: "authority-check",
          label: "Update authority verification",
          body: "Connect the wallet that holds update authority. IDX reads mint metadata state and confirms you can edit before exposing the form.",
        },
        {
          id: "field-edit",
          label: "Field editing and asset prep",
          body: "Revise name, symbol, image, description, tags, and social links within platform limits. Upload images to permanent storage and validate URIs before signing.",
        },
        {
          id: "on-chain-submit",
          label: "On-chain submission",
          body: "Sign the metadata update transaction from your wallet. IDX submits through your connected adapter with standard Solana network fees plus any platform charges shown in the UI.",
        },
        {
          id: "verify-refresh",
          label: "Verification and cache refresh",
          body: "Confirm changes on explorers, then re-check Jupiter, DexScreener, Token Hub, and Community Signal after indexer caches update. Document new links for support and marketing teams.",
        },
      ],
    },
    riskNotes: [
      "Only the update-authority wallet can submit changes — verify you connect the correct signer before editing.",
      "Broken or temporary image URIs degrade trust across wallets and DEX surfaces after you announce updates.",
      "Third-party indexers may lag behind on-chain confirmation; avoid announcing before explorer verification.",
      "Metadata updates do not fix supply, liquidity, or authority issues — address those with dedicated IDX tools.",
      "Major rebrands should be coordinated with Community Signal, social channels, and support macros to avoid mixed messaging.",
    ],
    decisionChecklist: [
      "Update authority wallet is connected and confirmed for the target mint.",
      "New image URI, description, and social links are production-ready and tested.",
      "Field lengths and tag selections meet IDX and on-chain constraints.",
      "Post-update verification plan includes explorers, Token Hub, and DexScreener checks.",
      "Community announcement draft and support macros reference updated metadata links.",
    ],
    related: [],
    extraRelatedLinks: [
      {
        href: "/token-creator",
        label: "Mint a new SPL token if redeploy is required",
      },
      {
        href: "/token-hub",
        label: "Re-verify mint after metadata changes",
      },
      {
        href: "/community",
        label: "Refresh Community Signal copy after rebrand",
      },
      {
        href: "/revoke-mint-authority",
        label: "Revoke mint authority after metadata is finalized",
      },
      {
        href: "/revoke-freeze",
        label: "Revoke freeze authority for holder trust signals",
      },
    ],
    heroCtaLabel: "Update SPL token metadata",
    faqCta: {
      label: "Open metadata editor",
    },
    faqHeading: "Solana Metadata Update FAQ",
    ogImage: "/metadata_update.png",
    ogImageAlt: "Update Solana token metadata guide — SPL and Token-2022",
  },
  "token-creator": {
    key: "token-creator",
    toolName: "Create SPL Token",
    toolPath: "/token-creator",
    learnMorePath: "/token-creator/learn-more",
    category: "Token Creation",
    primaryKeyword: "create spl token",
    description: "Learn the practical launch flow from token creation to liquidity and go-to-market.",
    summary: "A concise launch guide for teams creating a new SPL token.",
    audience: ["New token creators", "Founders"],
    steps: ["Define tokenomics.", "Create token and authorities.", "Prepare liquidity and launch operations."],
    faq: [
      { q: "What after token creation?", a: "Most teams move to liquidity setup, metadata polish, and launch comms." },
      { q: "Should I revoke authorities immediately?", a: "Only after confirming your operational plan; many teams revoke once setup and metadata are finalized." },
      { q: "What causes weak launches after token creation?", a: "Launching without liquidity planning, metadata quality, and a clear public communication path." },
    ],
    intentSections: [
      {
        title: "Launch-ready token creation flow",
        body: "Treat token creation as the first step in a launch system: mint setup, metadata quality, liquidity planning, and trust controls should be sequenced together.",
      },
      {
        title: "Post-mint priorities",
        body: "After minting, focus on metadata consistency, liquidity path, and authority policy so your token is ready for discovery and community trust.",
      },
    ],
    riskNotes: [
      "Double-check tokenomics values before irreversible mint actions.",
      "Separate creator wallet security from campaign operation wallets.",
      "Do not publish launch timing until metadata and liquidity steps are validated.",
    ],
    decisionChecklist: [
      "Token supply and decimals are finalized.",
      "Authority policy (mint/freeze) is documented.",
      "Metadata and branding assets are production-ready.",
      "Liquidity and community rollout plan is prepared.",
    ],
    related: [
      "crypto-creator",
      "crypto-token-creator",
      "liquidity-pool-creator",
      "update-metadata",
      "revoke-mint-authority",
    ],
    ogImage: "/token-creator.png",
  },
  "crypto-creator": {
    key: "crypto-creator",
    toolName: "Crypto Creator",
    toolPath: "/crypto-creator",
    learnMorePath: "/token-creator/learn-more",
    category: "Token Creation",
    primaryKeyword: "crypto creator solana",
    description:
      "Launch crypto projects on Solana with no-code SPL token creation, metadata, and authority controls.",
    summary: "Crypto creator for meme coins, utility tokens, and Web3 community launches on Solana.",
    audience: ["Crypto founders", "Community builders", "Web3 startups"],
    steps: [
      "Connect wallet and configure token details.",
      "Add branding and social links.",
      "Deploy and optionally revoke authorities.",
    ],
    faq: [
      {
        q: "How is Crypto Creator different from the SPL token creator?",
        a: "Both use the same launch flow; Crypto Creator is tailored for crypto project positioning and SEO.",
      },
    ],
    related: ["token-creator", "crypto-token-creator", "liquidity-pool-creator", "holders-maker"],
    ogImage: "/token-creator.png",
  },
  "crypto-token-creator": {
    key: "crypto-token-creator",
    toolName: "Crypto Token Creator",
    toolPath: "/crypto-token-creator",
    learnMorePath: "/token-creator/learn-more",
    category: "Token Creation",
    primaryKeyword: "crypto token creator solana",
    description:
      "Create SPL tokens on Solana instantly with metadata, social links, and optional authority revocation.",
    summary: "No-code crypto token creator for SPL tokens, meme coins, and utility assets.",
    audience: ["Token creators", "DeFi teams", "Meme coin launches"],
    steps: [
      "Enter token name, symbol, and supply.",
      "Upload image and add description.",
      "Create token and manage authorities.",
    ],
    faq: [
      {
        q: "Do I need coding skills?",
        a: "No. The crypto token creator is fully no-code and uses wallet-signed transactions.",
      },
    ],
    related: ["token-creator", "crypto-creator", "update-metadata", "liquidity-pool-creator"],
    ogImage: "/token-creator.png",
  },
  "token-creator-token-2022": {
    key: "token-creator-token-2022",
    toolName: "Create Token-2022 (Tax)",
    toolPath: "/token-creator/token-2022",
    learnMorePath: "/token-creator/token-2022/learn-more",
    category: "Token Creation",
    primaryKeyword: "create token 2022 solana",
    description: "Learn Token-2022 setup choices and compatibility considerations before deployment.",
    summary: "Understand advanced token configuration trade-offs for Token-2022 launches.",
    audience: ["Advanced creators", "Protocol teams"],
    steps: ["Review extension requirements.", "Configure controls.", "Validate downstream compatibility."],
    faq: [{ q: "Is Token-2022 always better?", a: "No. SPL can be better for simpler and broader compatibility needs." }],
    related: ["token-creator", "crypto-creator", "crypto-token-creator", "liquidity-pool-creator", "token-hub"],
    ogImage: "/token-creator.png",
  },
  "liquidity-pool-creator": {
    key: "liquidity-pool-creator",
    toolName: "Create Liquidity Pool",
    toolPath: "/liquidity-pool-creator",
    learnMorePath: "/liquidity-pool-creator/learn-more",
    category: "Liquidity",
    primaryKeyword: "create liquidity pool solana",
    seoTitle: "Create Raydium Liquidity Pool Guide | Solana",
    pageHeading: "Create Raydium Liquidity Pool Guide",
    metaDescription:
      "Create a Raydium CPMM pool on Solana: pick base and quote tokens, set SOL or stablecoin depth, validate slippage, and launch tradable pairs from IDX.",
    seoKeywords: [
      "create liquidity pool solana",
      "raydium pool creator",
      "solana cpmm pool",
      "add initial liquidity solana",
      "raydium liquidity guide",
      "create trading pair solana",
    ],
    description:
      "Create Liquidity Pool on IDX deploys Raydium CPMM pools for Solana SPL and Token-2022 base tokens paired with SOL, USDC, or USDT — with validation, slippage estimates, and wallet-signed pool creation.",
    summary:
      "This guide covers pair selection, initial liquidity sizing, duplicate-pool checks, price impact warnings, and post-creation verification so your token is tradable before volume bots or marketing campaigns go live.",
    primaryUseCase:
      "Use Create Liquidity Pool when your SPL token is minted and you need a Raydium CPMM pair with enough depth for early buyers before DexScreener discovery or volume automation.",
    audience: [
      "Token founders launching Raydium pairs after SPL mint and metadata setup",
      "Liquidity operators sizing initial SOL or stablecoin depth for launch day",
      "Teams graduating from bonding curves to AMM trading on Solana",
      "Projects planning OpenBook market creation and Raydium v4 routing alongside CPMM pools",
    ],
    steps: [
      "Connect your Solana wallet, select your base SPL or Token-2022 token, and choose a quote asset — SOL, USDC, or USDT.",
      "Enter initial base and quote amounts, review recommended ratios, price impact estimates, and minimum liquidity warnings from the validation panel.",
      "Confirm no duplicate pool exists for your pair, review Raydium CPMM creation fees and total SOL required including network costs.",
      "Sign the pool creation and initial deposit transactions from your wallet and wait for on-chain confirmation.",
      "Save your pool address, verify trading on Raydium and DexScreener, then proceed to volume bots, Token Hub verification, or liquidity locking.",
    ],
    faq: [
      {
        q: "Which pool type does IDX Create Liquidity Pool deploy?",
        a: "IDX deploys Raydium CPMM (constant product market maker) pools for Solana base tokens paired with SOL, USDC, or USDT. The tool validates quote mints, checks duplicates, and estimates creation fees before you sign.",
      },
      {
        q: "How much initial liquidity should I add?",
        a: "Enough depth to keep early slippage manageable for your expected launch traffic. Underfunded pools produce harsh price impact on first buys and weak execution for volume bots. Use the Liquidity Simulator to model scenarios before committing capital.",
      },
      {
        q: "Can I create a pool for Token-2022 mints?",
        a: "Yes, when your Token-2022 mint is supported by Raydium CPMM routes and passes IDX validation. Verify program compatibility and blacklist checks before signing.",
      },
      {
        q: "What happens if a pool already exists for my pair?",
        a: "IDX checks for duplicate pools and warns you before creation. Adding to an existing pool is usually better than fragmenting liquidity across multiple CPMM pairs for the same base and quote.",
      },
      {
        q: "How much SOL do I need beyond the liquidity deposit?",
        a: "Budget for Raydium CPMM pool creation fees, wrapped SOL account rent if applicable, and standard Solana transaction fees. The confirmation panel shows estimated total SOL before you sign.",
      },
      {
        q: "When should I create the pool relative to launch marketing?",
        a: "Create the pool when metadata, authority policy, and launch communications are ready — typically immediately before or at the start of your public launch window so discovery traffic finds a tradable pair.",
      },
      {
        q: "What should I do after the pool is live?",
        a: "Verify the pool on Raydium and DexScreener, document the pool address in Token Hub, consider locking LP tokens, and run volume or holder campaigns only after confirming swap routes work with your expected trade sizes.",
      },
    ],
    intentSections: [
      {
        title: "Raydium CPMM pool creation on Solana",
        body: "Create Liquidity Pool on IDX is the practical step that makes your SPL token swappable on Raydium. CPMM pools use the constant-product formula — depositing base tokens and a quote asset (SOL, USDC, or USDT) establishes the initial price curve traders interact with. Without adequate pool depth, even strong marketing sends users to pairs with punishing slippage. This guide focuses on sizing, validation, and timing so your first pool supports real trading rather than acting as a placeholder.",
      },
      {
        title: "Capital planning before pool creation",
        body: "Initial liquidity is both a treasury decision and a user experience decision. Teams often simulate buy scenarios with the Liquidity Simulator to see how much SOL a launch buyer needs to move price by five or ten percent. Compare simulation output to your marketing expectations — if a typical community buy would move price uncomfortably, increase deposit size or adjust launch narrative timing. Remember pool creation fees and wrapped SOL rent sit on top of the liquidity you deposit.",
      },
      {
        title: "Validation, duplicates, and warnings",
        body: "IDX runs duplicate-pool detection, minimum liquidity checks, rate limits, and price impact warnings before you sign. Treat warnings seriously: they flag conditions that cause failed transactions or poor trader experience. If validation suggests an existing pool, add liquidity to that pool instead of splitting depth. Blacklisted or unsupported tokens fail early with clear errors so you do not waste SOL on doomed submissions.",
      },
      {
        title: "Post-creation launch sequencing",
        body: "After confirmation, your operational sequence typically includes DexScreener visibility checks, Token Hub mint verification, optional LP locking for trust signals, and growth tools like volume bots or Reaction Booster. OpenBook market creation may precede or follow CPMM setup depending on your Raydium v4 plans — document pool ID, mint, and quote pair in one launch runbook so support and mods answer from consistent links.",
      },
    ],
    engineGuide: {
      title: "Create Liquidity Pool components",
      intro:
        "Pool creation combines token selection, liquidity sizing, on-chain deployment, and post-launch verification. Each component affects whether early traders experience smooth execution.",
      engines: [
        {
          id: "pair-select",
          label: "Pair and quote selection",
          body: "Pick your SPL or Token-2022 base token and a quote mint — SOL for native pairs, USDC or USDT for stablecoin-denominated pricing. Quote choice affects how traders think about price display.",
        },
        {
          id: "liquidity-size",
          label: "Initial liquidity sizing",
          body: "Enter base and quote deposit amounts. IDX shows recommended ratios, price impact, and minimum liquidity guidance. Use the Liquidity Simulator for pre-deployment modeling.",
        },
        {
          id: "validate-create",
          label: "Validation and creation",
          body: "Duplicate checks, blacklist validation, and fee estimates run before signing. Approve wallet transactions to create the CPMM pool and seed initial reserves in one flow.",
        },
        {
          id: "verify-launch",
          label: "Verification and launch handoff",
          body: "Confirm pool address on Raydium and DexScreener, save records in Token Hub, and proceed to locking, volume automation, or OpenBook setup with documented pool context.",
        },
      ],
    },
    riskNotes: [
      "Underfunded initial pools cause severe slippage and weak execution during high-visibility launches.",
      "Duplicate pools fragment liquidity — add to existing pairs when validation warns a match exists.",
      "Pool creation consumes SOL for fees and rent beyond the tokens deposited as liquidity.",
      "Impermanent loss and market risk remain after creation — locking LP does not eliminate price risk.",
      "Verify token metadata and mint address before depositing — incorrect pairs cannot be undone without new pools.",
    ],
    decisionChecklist: [
      "Base mint, quote asset, and decimals are confirmed on-chain.",
      "Initial liquidity budget is approved and simulated where possible.",
      "Duplicate-pool and validation warnings are reviewed before signing.",
      "Total SOL required includes creation fees, rent, and network costs.",
      "Post-creation runbook includes DexScreener, Token Hub, and campaign links.",
    ],
    related: [],
    extraRelatedLinks: [
      {
        href: "/liquidity-pool-creator/liquidity-simulator",
        label: "Model pool depth before deploying capital",
      },
      {
        href: "/create-openbook-market",
        label: "Register OpenBook market for Raydium v4 routing",
      },
      {
        href: "/lock-liquidity",
        label: "Lock LP tokens after pool creation",
      },
      {
        href: "/volume-bot",
        label: "Seed chart activity after liquidity goes live",
      },
      {
        href: "/manage-liquidity/add-liquidity",
        label: "Add more depth to an existing Raydium pair",
      },
    ],
    heroCtaLabel: "Create Raydium liquidity pool",
    faqCta: {
      label: "Open pool creator tool",
    },
    faqHeading: "Raydium Liquidity Pool FAQ",
    ogImage: "/liquidity-pool-creator.png",
    ogImageAlt: "Create Raydium liquidity pool on Solana guide",
  },
  "liquidity-simulator": {
    key: "liquidity-simulator",
    toolName: "Liquidity Simulator",
    toolPath: "/liquidity-pool-creator/liquidity-simulator",
    learnMorePath: "/liquidity-pool-creator/liquidity-simulator/learn-more",
    category: "Liquidity",
    primaryKeyword: "liquidity simulator solana",
    seoTitle: "Solana Liquidity Simulator Guide | Pool Modeling",
    pageHeading: "Solana Liquidity Simulator Guide",
    metaDescription:
      "Solana liquidity simulator: model Raydium CPMM pool depth, buy impact, and SOL or stablecoin quotes before you create a live pool on IDX.",
    seoKeywords: [
      "liquidity simulator solana",
      "amm pool simulator",
      "raydium liquidity calculator",
      "pool depth modeling solana",
      "slippage simulator solana",
      "liquidity planning tool",
    ],
    description:
      "Liquidity Simulator on IDX models constant-product AMM behavior for Solana pool planning — adjust token supply, initial SOL/USDC/USDT depth, and simulated buy sizes to preview price impact before live deployment.",
    summary:
      "This guide explains how to input supply and quote assumptions, interpret buy-impact tables, compare SOL versus stablecoin quotes, and translate simulation insights into Create Liquidity Pool deposit decisions.",
    primaryUseCase:
      "Use Liquidity Simulator before Create Liquidity Pool when you need to estimate how much quote liquidity prevents unacceptable slippage on launch-day buy sizes.",
    audience: [
      "Founders sizing initial Raydium pool depth before committing treasury SOL",
      "Liquidity managers comparing SOL, USDC, and USDT quote scenarios",
      "Launch teams validating whether marketing promises match AMM math",
      "Operators pairing simulation output with volume bot and holder campaign timing",
    ],
    steps: [
      "Open Liquidity Simulator from the pool creator section and set your total token supply and planned pool token allocation.",
      "Choose a quote asset — SOL, USDC, or USDT — and enter initial quote liquidity within the simulator range.",
      "Adjust simulated buy size as a percentage of the token reserve to see quote spend, tokens received, and price change.",
      "Compare scenarios across different quote depths and buy percentages to find acceptable slippage for your launch narrative.",
      "Apply the chosen base and quote amounts in Create Liquidity Pool and re-verify live price impact at deployment time.",
    ],
    faq: [
      {
        q: "What does the Solana Liquidity Simulator calculate?",
        a: "The simulator uses constant-product AMM math (x*y=k) to model how much SOL, USDC, or USDT a buyer spends to acquire a percentage of the token side of a hypothetical pool, and how much price moves after that trade.",
      },
      {
        q: "Does simulation guarantee live pool outcomes?",
        a: "No. Simulations improve planning but live markets add fees, routing, MEV, concurrent trades, and external liquidity flows that models omit. Always re-check validation output when you create the real pool.",
      },
      {
        q: "Which quote assets can I model?",
        a: "You can simulate SOL, USDC, and USDT quote sides with per-asset min/max ranges and USD conversion displays. SOL USD reference price updates when live price feeds are available.",
      },
      {
        q: "How should I use simulator output for launch planning?",
        a: "Identify the minimum quote depth where a typical community buy (expressed as percent of pool tokens) produces acceptable price impact. If only large buys look reasonable, increase planned liquidity or adjust launch expectations.",
      },
      {
        q: "Can I simulate Token-2022 or meme supply extremes?",
        a: "The simulator accepts supply and allocation inputs across a wide range so teams with billion-token supplies or low-float launches can explore extremes. Validate final parameters against IDX pool creator limits before signing.",
      },
      {
        q: "How does the simulator connect to Create Liquidity Pool?",
        a: "Use simulation to pick base and quote deposit sizes, then enter those amounts in Create Liquidity Pool. The live tool adds duplicate checks, fee estimates, and on-chain validation that simulation does not replace.",
      },
      {
        q: "Should I simulate before or after OpenBook market creation?",
        a: "Simulate CPMM depth before Raydium pool creation regardless of OpenBook timing. Orderbook markets and CPMM pools serve different routing layers — model AMM depth for swap execution quality on your primary pool.",
      },
    ],
    intentSections: [
      {
        title: "AMM modeling before capital commitment",
        body: "Liquidity Simulator exists because founders often discover slippage problems only after depositing treasury funds into a live Raydium pool. By modeling constant-product reserves upfront, you see how a five, ten, or twenty percent buy against your token side consumes quote liquidity and shifts price. That preview helps you right-size SOL or stablecoin deposits, set honest community expectations, and avoid launch-day surprises when DexScreener traffic arrives.",
      },
      {
        title: "Interpreting buy impact and price change",
        body: "The simulator shows quote spend, tokens received, new pool price, and percentage price change for each buy scenario. Large price jumps on modest buys signal underfunded pools. Compare multiple quote depths side by side — sometimes doubling SOL liquidity dramatically flattens impact curves. Pay attention to USD displays when modeling USDC or USDT quotes so treasury planning stays denominated in familiar terms.",
      },
      {
        title: "SOL versus stablecoin quote planning",
        body: "SOL pairs dominate meme and native Solana launches; stablecoin quotes help teams who want price displayed in USD terms on aggregators. The simulator's per-quote configuration reflects different min/max ranges and decimal formatting. Choose the quote asset you plan to use in Create Liquidity Pool so simulation maps directly to deployment — switching quote assets after simulation requires rerunning scenarios.",
      },
      {
        title: "From simulation to live deployment",
        body: "Treat simulator output as a decision input, not a guarantee. After you pick deposit sizes, Create Liquidity Pool adds duplicate detection, blacklist validation, Raydium fees, and wallet-signed execution. Re-read price impact warnings at confirmation time because market conditions and fee tiers may differ slightly from the model. Document chosen parameters in your launch runbook alongside pool address and volume bot settings.",
      },
    ],
    engineGuide: {
      title: "Liquidity Simulator inputs",
      intro:
        "Four input groups drive simulation output: supply allocation, quote depth, buy sizing, and quote asset selection. Adjusting each group shows how AMM math responds before you deploy capital.",
      engines: [
        {
          id: "supply-allocation",
          label: "Supply and pool allocation",
          body: "Set total token supply and how many tokens you plan to deposit in the pool. Allocation defines the token-side reserve (A) in the constant-product formula.",
        },
        {
          id: "quote-depth",
          label: "Quote liquidity depth",
          body: "Enter initial SOL, USDC, or USDT on the quote side (B). Depth directly controls how much quote a buyer must spend for a given token purchase percentage.",
        },
        {
          id: "buy-scenario",
          label: "Buy scenario sizing",
          body: "Simulate buys as a fraction of the token reserve removed from the pool. Output shows spend, received tokens, new price, and percentage price change.",
        },
        {
          id: "quote-compare",
          label: "Quote asset comparison",
          body: "Switch between SOL, USDC, and USDT to compare how quote denomination affects displayed prices and USD estimates before you pick a live pair.",
        },
      ],
    },
    riskNotes: [
      "Simulations omit live fees, concurrent trades, and routing effects — re-validate at pool creation time.",
      "Extreme buy percentages in simulation may not reflect realistic community behavior — model multiple scenarios.",
      "USD displays depend on reference prices; SOL USD may use fallback values when feeds are unavailable.",
      "Simulator output does not replace professional treasury or market-making advice for large launches.",
      "Underfunded live pools still fail launch goals even if small simulated buys look acceptable.",
    ],
    decisionChecklist: [
      "Supply, pool allocation, and quote asset match your Create Liquidity Pool plan.",
      "Simulated buy sizes reflect realistic launch-day community trade expectations.",
      "Acceptable price impact thresholds are defined before treasury sign-off.",
      "Compared SOL, USDC, and USDT scenarios if quote choice is undecided.",
      "Chosen deposit sizes are documented for the live pool creation step.",
    ],
    related: [],
    extraRelatedLinks: [
      {
        href: "/liquidity-pool-creator",
        label: "Deploy the Raydium pool you modeled here",
      },
      {
        href: "/create-openbook-market",
        label: "Add OpenBook routing for Raydium v4 setups",
      },
      {
        href: "/lock-liquidity",
        label: "Lock LP after confirming simulated depth",
      },
      {
        href: "/volume-bot",
        label: "Plan volume automation after pool goes live",
      },
      {
        href: "/token-creator",
        label: "Create SPL token before pool simulation",
      },
    ],
    heroCtaLabel: "Run liquidity simulation",
    faqCta: {
      label: "Open Liquidity Simulator",
    },
    faqHeading: "Liquidity Simulator FAQ",
    ogImage: "/liquidity_simulator.png",
    ogImageAlt: "Solana liquidity simulator — model pool depth and buy impact",
  },
  "manage-liquidity-add": {
    key: "manage-liquidity-add",
    toolName: "Add Liquidity",
    toolPath: "/manage-liquidity/add-liquidity",
    learnMorePath: "/manage-liquidity/add-liquidity/learn-more",
    category: "Liquidity Management",
    primaryKeyword: "add liquidity solana",
    description: "Learn how to add liquidity to existing pools with cleaner ratio and slippage handling.",
    summary: "A practical guide for safer liquidity deposits.",
    audience: ["Liquidity providers", "Token teams"],
    steps: ["Validate pool.", "Input balanced amounts.", "Confirm and monitor LP position."],
    faq: [{ q: "Why do deposit ratios matter?", a: "Correct ratios reduce rejects and keep deposits aligned with pool pricing." }],
    related: ["manage-liquidity-remove", "liquidity-pool-creator", "lock-liquidity"],
    ogImage: "/liquidity_manager.png",
  },
  "manage-liquidity-remove": {
    key: "manage-liquidity-remove",
    toolName: "Remove Liquidity",
    toolPath: "/manage-liquidity/remove-liquidity",
    learnMorePath: "/manage-liquidity/remove-liquidity/learn-more",
    category: "Liquidity Management",
    primaryKeyword: "remove liquidity solana",
    description: "Learn how to remove liquidity with better timing, sizing, and slippage awareness.",
    summary: "Plan exits and rebalancing with clearer LP handling.",
    audience: ["Liquidity providers", "Treasury operators"],
    steps: ["Validate LP balance.", "Choose amount.", "Confirm and verify received assets."],
    faq: [{ q: "What happens when removing liquidity?", a: "LP shares are redeemed for underlying assets based on your pool share." }],
    related: ["manage-liquidity-add", "lock-liquidity", "liquidity-simulator"],
    ogImage: "/liquidity_manager.png",
  },
  "revoke-mint-authority": {
    key: "revoke-mint-authority",
    toolName: "Revoke Mint Authority",
    toolPath: "/revoke-mint-authority",
    learnMorePath: "/revoke-mint-authority/learn-more",
    category: "Security & Trust",
    primaryKeyword: "revoke mint authority solana",
    seoTitle: "Revoke Mint Authority on Solana | Trust Guide",
    pageHeading: "Revoke Mint Authority on Solana Guide",
    metaDescription:
      "Revoke mint authority on Solana: verify supply caps, sign the revoke with your creator wallet, publish explorer proof, and strengthen holder trust.",
    seoKeywords: [
      "revoke mint authority solana",
      "solana mint authority revoke",
      "disable token minting solana",
      "fixed supply spl token",
      "mint authority null solana",
      "token trust signals solana",
    ],
    description:
      "Revoke Mint Authority on IDX permanently removes the ability to mint additional SPL or Token-2022 supply — a standard trust signal Solana communities verify on explorers before allocating attention or liquidity.",
    summary:
      "This guide explains when to revoke mint authority, how IDX builds client-side transactions from your connected wallet, what irreversibility means for supply, and how to publish proof links after confirmation.",
    primaryUseCase:
      "Use Revoke Mint Authority after tokenomics, metadata, and initial distribution are finalized and you want holders to verify fixed supply on Solana explorers.",
    audience: [
      "Token founders publishing fixed-supply trust signals before marketing scale",
      "Community leads answering holder questions about inflation risk",
      "Projects completing launch checklists alongside freeze authority revocation",
      "Teams transitioning from test mints to production tokens with verified supply caps",
    ],
    steps: [
      "Connect the wallet that currently holds mint authority for your SPL or Token-2022 token.",
      "Select the token from your wallet list and confirm IDX displays the current mint authority state as revokable.",
      "Review the irreversibility notice, platform fee, and network fee estimate before approving the transaction.",
      "Sign the revoke transaction — IDX reassembles instructions client-side so your wallet shows a clear approval dialog.",
      "Verify mint authority is null on Solana explorers, share proof links with your community, and update Token Hub context.",
    ],
    faq: [
      {
        q: "What happens when I revoke mint authority on Solana?",
        a: "Revoking mint authority permanently removes the ability to create new tokens for that mint. Total supply becomes fixed at the current circulating amount. This action is irreversible in standard SPL and Token-2022 setups.",
      },
      {
        q: "Which wallet must sign the revoke transaction?",
        a: "The connected wallet must be the current mint authority holder — typically the wallet that created the token. IDX validates access before building the revoke flow and shows an error if the wrong wallet is connected.",
      },
      {
        q: "Can revoked mint authority be restored?",
        a: "No. Once mint authority is set to null on-chain, no wallet can mint additional supply for that mint. Treat revocation as final and confirm tokenomics before signing.",
      },
      {
        q: "When should teams revoke mint authority?",
        a: "Most teams revoke after metadata, initial allocations, and liquidity plans are complete — often alongside or before freeze authority revocation and major marketing pushes. Revoking too early can block legitimate supply adjustments you still need.",
      },
      {
        q: "Does IDX charge a fee to revoke mint authority?",
        a: "IDX charges a platform fee (shown in the UI, typically 0.1 SOL) plus standard Solana network fees. The total required balance is displayed before you sign.",
      },
      {
        q: "How do holders verify mint authority was revoked?",
        a: "Anyone can check the mint account on Solana explorers — mint authority should show as null or disabled after confirmation. Share the explorer link in announcements and pin it in community channels.",
      },
      {
        q: "Should I revoke mint authority before or after adding liquidity?",
        a: "Order depends on your tokenomics. Many teams finalize mint allocations and pool deposits first, then revoke mint authority before public trust messaging scales. Document the sequence in your launch runbook so support answers consistently.",
      },
    ],
    intentSections: [
      {
        title: "Fixed supply as a Solana trust signal",
        body: "Mint authority revocation is one of the most recognized trust signals in Solana token launches. When mint authority is null, holders and scanners can verify that no party can inflate supply after marketing begins. IDX Revoke Mint Authority provides a no-code, wallet-signed path to that state with transparent fees and client-side transaction assembly so Phantom and other wallets display clear approval dialogs rather than opaque server blobs.",
      },
      {
        title: "Timing revocation in your launch checklist",
        body: "Revoke too early and you may still need mint adjustments for treasury, liquidity, or partnership allocations. Revoke too late and skeptical traders may delay engagement until supply risk is eliminated. A practical sequence: finalize metadata, complete initial mints and pool setup, verify circulating supply on explorers, then revoke mint authority before DexScreener promotion, volume bots, or exchange outreach. Pair the action with a public post linking explorer proof.",
      },
      {
        title: "Irreversibility and communication",
        body: "Teams must internalize that revocation is permanent. Internal docs should record the transaction signature, timestamp, and explorer URL. Community messaging should explain fixed supply in plain language without implying revocation alone guarantees price performance or eliminates all project risk. Combine mint revoke with freeze revoke, metadata quality, and liquidity transparency for a coherent trust narrative.",
      },
      {
        title: "Connecting revoke to the IDX launch stack",
        body: "After revocation, re-verify your mint in Token Hub so downstream tools and Community Signal pages reflect current authority state. Holders Maker, volume bots, and Reaction Booster campaigns run smoother when your public story matches on-chain facts. If you update metadata after revoking mint authority, you still need update authority — revocation affects minting only, not metadata edits.",
      },
    ],
    engineGuide: {
      title: "Revoke Mint Authority workflow",
      intro:
        "Mint authority revocation moves through wallet verification, state confirmation, signed revoke, and public proof. Each step is irreversible once the transaction confirms.",
      engines: [
        {
          id: "wallet-verify",
          label: "Mint authority wallet verification",
          body: "Connect the wallet holding mint authority. IDX reads on-chain mint state and confirms you can revoke before exposing the action button.",
        },
        {
          id: "state-review",
          label: "Authority state review",
          body: "Review current supply, mint authority address, and irreversibility warnings. Confirm tokenomics are final before proceeding.",
        },
        {
          id: "sign-revoke",
          label: "Client-side revoke transaction",
          body: "IDX extracts backend instructions and rebuilds a VersionedTransaction client-side for wallet approval. Sign once to set mint authority to null.",
        },
        {
          id: "proof-publish",
          label: "Explorer proof and comms",
          body: "Verify null mint authority on Solana explorers, save the transaction signature, and publish proof links in community channels and Token Hub documentation.",
        },
      ],
    },
    riskNotes: [
      "Mint authority revocation is permanent — confirm supply and allocations are final before signing.",
      "Only the current mint authority wallet can revoke — connecting the wrong wallet fails or risks confusion.",
      "Revocation does not disable metadata updates if update authority is still held elsewhere.",
      "Fixed supply does not eliminate market risk, rug patterns via liquidity, or other trust considerations.",
      "Platform and network fees apply even if you abandon the flow after partial steps — review totals before approve.",
    ],
    decisionChecklist: [
      "Tokenomics and circulating supply are finalized on-chain.",
      "Correct mint authority wallet is connected in IDX.",
      "Team understands revocation is irreversible.",
      "Explorer proof link and community announcement are drafted.",
      "Token Hub and marketing materials will be updated after confirmation.",
    ],
    related: [],
    extraRelatedLinks: [
      {
        href: "/revoke-freeze",
        label: "Revoke freeze authority for full trust stack",
      },
      {
        href: "/update-metadata",
        label: "Finalize metadata before authority revokes",
      },
      {
        href: "/token-hub",
        label: "Verify mint state after mint authority revoke",
      },
      {
        href: "/liquidity-pool-creator",
        label: "Add Raydium liquidity after supply is fixed",
      },
      {
        href: "/token-creator",
        label: "Create SPL token with planned authority policy",
      },
    ],
    heroCtaLabel: "Revoke mint authority now",
    faqCta: {
      label: "Open mint authority revoke tool",
    },
    faqHeading: "Revoke Mint Authority FAQ",
    ogImage: "/revoke_mint_authority.png",
    ogImageAlt: "Revoke mint authority on Solana — fixed supply trust guide",
  },
  "revoke-freeze": {
    key: "revoke-freeze",
    toolName: "Revoke Freeze Authority",
    toolPath: "/revoke-freeze",
    learnMorePath: "/revoke-freeze/learn-more",
    category: "Security & Trust",
    primaryKeyword: "revoke freeze authority solana",
    seoTitle: "Revoke Freeze Authority on Solana | Trust Guide",
    pageHeading: "Revoke Freeze Authority on Solana Guide",
    metaDescription:
      "Revoke freeze authority on Solana: remove freeze on holder accounts, sign with your wallet, publish explorer proof, and improve SPL token trust.",
    seoKeywords: [
      "revoke freeze authority solana",
      "solana freeze authority revoke",
      "disable token freeze solana",
      "spl token freeze revoke",
      "unfreeze holder trust solana",
      "token safety signals solana",
    ],
    description:
      "Revoke Freeze Authority on IDX permanently removes the ability to freeze holder token accounts — a critical transparency step Solana traders check alongside mint authority when evaluating new SPL and Token-2022 projects.",
    summary:
      "This guide covers freeze authority risks, when to revoke, IDX client-side transaction flow, irreversibility, and how to communicate proof to holders after your wallet signs the on-chain revoke.",
    primaryUseCase:
      "Use Revoke Freeze Authority when your token is live and you want holders to verify that no party can freeze wallets holding your SPL or Token-2022 mint.",
    audience: [
      "Token founders removing freeze risk before scaling community and marketing",
      "Community moderators answering holder questions about account freeze powers",
      "Projects pairing freeze revoke with mint authority revoke in trust announcements",
      "Launch teams completing security checklists before DexScreener and volume campaigns",
    ],
    steps: [
      "Connect the wallet that currently holds freeze authority for your SPL or Token-2022 mint.",
      "Select the token and confirm IDX shows freeze authority as held by your connected wallet.",
      "Read the irreversibility notice, review platform and network fee estimates, and confirm you understand freeze power will be permanently removed.",
      "Sign the revoke transaction built client-side by IDX so your wallet displays transparent instruction approval.",
      "Verify freeze authority is null on Solana explorers, publish proof links, and update Token Hub and community pins.",
    ],
    faq: [
      {
        q: "What is freeze authority on Solana tokens?",
        a: "Freeze authority allows the designated wallet to freeze token accounts, preventing transfers until unfrozen. Holders often treat retained freeze authority as elevated risk because funds could theoretically be locked.",
      },
      {
        q: "What happens when I revoke freeze authority?",
        a: "Revoking sets freeze authority to null permanently. No wallet can freeze new or existing holder accounts for that mint afterward. This is irreversible in standard SPL and Token-2022 configurations.",
      },
      {
        q: "Which wallet must sign the freeze revoke?",
        a: "The connected wallet must hold the current freeze authority — usually the token creator wallet. IDX validates authority before enabling the revoke action.",
      },
      {
        q: "Should I revoke freeze before or after mint authority?",
        a: "Many teams revoke both during the same trust phase after metadata and liquidity are ready. Order is less important than completing both before major marketing, but document whichever sequence you choose for consistent community answers.",
      },
      {
        q: "Does revoking freeze authority affect minting?",
        a: "No. Freeze revoke and mint revoke are separate actions. You must revoke mint authority separately to fix supply. Update authority is also independent — metadata can still be edited if update authority is held.",
      },
      {
        q: "How do traders verify freeze authority was revoked?",
        a: "Solana explorers display freeze authority status on the mint account. After confirmation, authority should show as null. Share the explorer link in announcements and add it to your Token Hub documentation.",
      },
      {
        q: "Does IDX charge a platform fee for freeze revoke?",
        a: "Yes. IDX displays a platform fee (typically 0.1 SOL) plus network fees before you sign. Ensure your wallet holds enough SOL for the full amount.",
      },
    ],
    intentSections: [
      {
        title: "Why freeze authority matters to Solana holders",
        body: "Freeze authority is the power to lock token accounts so holders cannot transfer balances. Even if a team never intends to use it, retained freeze authority signals theoretical control that scanners and experienced traders flag. Revoking freeze authority demonstrates that holder balances cannot be frozen by the project wallet going forward. IDX provides a straightforward revoke flow with client-side transaction assembly so wallet approvals remain transparent.",
      },
      {
        title: "Pairing freeze revoke with mint revoke",
        body: "Trust messaging lands best when mint and freeze revokes appear together in a single coherent announcement with explorer links for both. Fixed supply plus no freeze power addresses the two most common automated risk checks on Solana token dashboards. Complete metadata polish and liquidity steps first so your trust post accompanies a tradable, well-branded token rather than an empty shell.",
      },
      {
        title: "Irreversibility and holder communication",
        body: "Like mint revocation, freeze revocation cannot be undone. Internal runbooks should store transaction signatures and explorer URLs. Public posts should explain in plain language that holder accounts cannot be frozen after confirmation — without claiming revocation guarantees investment returns or eliminates all smart-contract risks outside SPL freeze scope.",
      },
      {
        title: "Freeze revoke in the IDX launch workflow",
        body: "After revoking freeze authority, re-verify mint status in Token Hub and refresh Community Signal pages if authority badges or copy reference security state. Proceed to volume bots, Reaction Booster, or holder campaigns when on-chain facts align with marketing. Support teams should use the same explorer links mods pin in Telegram to reduce repetitive authority questions.",
      },
    ],
    engineGuide: {
      title: "Revoke Freeze Authority workflow",
      intro:
        "Freeze authority revocation follows wallet verification, on-chain state review, signed revoke, and public proof — mirroring mint revoke with separate authority checks.",
      engines: [
        {
          id: "freeze-wallet",
          label: "Freeze authority wallet check",
          body: "Connect the wallet holding freeze authority. IDX confirms on-chain state matches your signer before showing the revoke action.",
        },
        {
          id: "risk-review",
          label: "Risk and irreversibility review",
          body: "Read warnings that freeze power will be permanently removed. Confirm no operational reason remains to retain freeze authority.",
        },
        {
          id: "client-revoke",
          label: "Client-side transaction signing",
          body: "IDX rebuilds revoke instructions into a VersionedTransaction for wallet approval. Sign once to null freeze authority on the mint.",
        },
        {
          id: "holder-proof",
          label: "Holder-facing proof publication",
          body: "Verify null freeze authority on explorers, distribute links to community channels, and sync Token Hub records for support consistency.",
        },
      ],
    },
    riskNotes: [
      "Freeze authority revocation is permanent — you cannot regain freeze power after confirmation.",
      "Wrong wallet connection fails the flow; verify freeze authority holder before signing.",
      "Revoking freeze does not revoke mint or update authority — handle each separately.",
      "Null freeze authority reduces one risk vector but does not eliminate market or liquidity risks.",
      "Platform and network fees apply; review wallet balance before approving the transaction.",
    ],
    decisionChecklist: [
      "Freeze authority holder wallet is connected and verified.",
      "Team agrees freeze power is no longer needed operationally.",
      "Irreversibility is understood by founders and community leads.",
      "Explorer proof URL and announcement copy are prepared.",
      "Token Hub and pinned resources will reflect updated authority state.",
    ],
    related: [],
    extraRelatedLinks: [
      {
        href: "/revoke-mint-authority",
        label: "Fix supply with mint authority revocation",
      },
      {
        href: "/update-metadata",
        label: "Polish metadata before trust announcements",
      },
      {
        href: "/token-hub",
        label: "Confirm authority state in mint verification hub",
      },
      {
        href: "/community",
        label: "Share trust proof on Community Signal pages",
      },
      {
        href: "/liquidity-pool-creator",
        label: "Launch Raydium pool after trust steps complete",
      },
    ],
    heroCtaLabel: "Revoke freeze authority now",
    faqCta: {
      label: "Open freeze authority revoke tool",
    },
    faqHeading: "Revoke Freeze Authority FAQ",
    ogImage: "/revoke_freeze.png",
    ogImageAlt: "Revoke freeze authority on Solana — holder trust guide",
  },
  "token-hub": {
    key: "token-hub",
    toolName: "Token Hub",
    toolPath: "/token-hub",
    learnMorePath: "/token-hub/learn-more",
    category: "Token Operations",
    primaryKeyword: "token hub solana",
    seoTitle: "Token Hub for Solana | Verify Mint & Tools Guide",
    metaDescription:
      "Token Hub for Solana: verify SPL mints, open chart and swap tools, route to Community Signal, and keep one canonical token context for launch teams.",
    seoKeywords: [
      "token hub solana",
      "verify solana mint",
      "solana token verification",
      "token hub guide",
      "community signal mint",
      "solana token operations",
    ],
    description:
      "Token Hub for Solana verifies SPL mint addresses and opens creator workflows — chart, swap, Community Signal generation, and downstream IDX tools — from one operational center.",
    summary:
      "This guide explains how Token Hub centralizes mint verification, token status review, and action routing so Solana teams stop juggling scattered mint links across marketing, support, and launch tooling.",
    primaryUseCase:
      "Use Token Hub when you need one verified mint entry point before chart, swap, Community Signal, volume, or holder workflows — so teams stop pasting the same address into scattered tools.",
    audience: [
      "Token founders who need one verified mint entry point for launch tooling",
      "Community and marketing leads sharing consistent token context",
      "Project admins routing support tickets to the correct mint and hub pages",
      "Operators connecting verification to metadata, volume, and holder workflows",
    ],
    steps: [
      "Open Token Hub and paste your Solana SPL mint address — no wallet required for initial verification.",
      "Review returned token status, symbol, and verification context before sharing links publicly.",
      "Open downstream workflows: token chart hub, Fast Swap, Community Signal generation, or related IDX tools.",
      "Confirm canonical hub or Community Signal URLs before campaigns, and re-verify after metadata or authority changes.",
    ],
    faq: [
      {
        q: "What does Token Hub for Solana do?",
        a: "Token Hub verifies a Solana mint and opens creator workflows — chart, Fast Swap, Community Signal pages, and links to metadata, volume, and holder tools — from one landing. No wallet is required for verification; downstream actions may still ask you to connect.",
      },
      {
        q: "Who should use Token Hub?",
        a: "Both new and experienced teams. Beginners get a guided verify-then-act flow; operators use it as the canonical mint router so support and marketing share the same verified context.",
      },
      {
        q: "Can I generate a Community Signal page from Token Hub?",
        a: "Yes. After verification, Token Hub routes you to generate or visit a Community Signal page for your mint — a share-ready destination for social and discovery workflows.",
      },
      {
        q: "When should teams re-check Token Hub after launch?",
        a: "Re-verify after metadata updates, authority revokes, liquidity changes, or rebrands so hub output and public links stay aligned with on-chain state.",
      },
    ],
    intentSections: [
      {
        title: "Operational control center for Solana tokens",
        body: "Use Token Hub as the source of truth for token status, verification, and action routing. Instead of bookmarking many tool URLs with the same mint pasted repeatedly, confirm the asset once, then branch into chart, swap, Community Signal, or management tools. That consistency also cleans up community posts and support macros — fewer wrong mints and stale symbols.",
      },
      {
        title: "Verify before automation and launch stack routing",
        body: "Run holder campaigns, volume bots, or metadata updates only after Token Hub confirms the mint. Verification is a lightweight gate against funding the wrong bot or publishing a Community Signal page for a test token. After you create an SPL token and add liquidity, hub → Community Signal → growth tools keeps one address context across the stack.",
      },
    ],
    engineGuide: {
      title: "Token Hub workflow components",
      intro:
        "Token Hub combines mint verification with routed access to creator tools. Use this verify-then-act checklist before campaigns go live.",
      engines: [
        {
          id: "mint-verify",
          label: "Mint verification and market access",
          body: "Paste a Solana SPL mint to validate format and fetch token context, then open chart and Fast Swap paths tied to that mint so teams stop hunting DEX URLs manually.",
        },
        {
          id: "signal-and-tools",
          label: "Community Signal and downstream tools",
          body: "Generate or visit a Community Signal page for the verified mint, then route into metadata, volume, holder, or creation flows with the same context support and marketing already trust.",
        },
      ],
    },
    riskNotes: [
      "Keep Token Hub records and public Community Signal pages current after metadata or authority changes.",
      "Align hub output with website and social copy to prevent contradictory token messaging.",
      "Assign ownership for periodic re-verification before major campaigns or paid promotions.",
      "Verification confirms mint context — it does not replace legal review or investment disclosures.",
      "Downstream tools may require wallet connection and SOL fees; verify mint first, then fund actions deliberately.",
    ],
    decisionChecklist: [
      "SPL mint address is validated in Token Hub before campaign use.",
      "Token name, symbol, and status match public marketing materials.",
      "Community Signal or hub URLs are canonical and tested in a private channel first.",
      "Downstream tool links (swap, metadata, volume, holders) open the intended mint.",
      "A team owner is assigned to re-verify after metadata or liquidity changes.",
    ],
    related: [],
    extraRelatedLinks: [
      {
        href: "/community",
        label: "Generate a Community Signal page from your verified mint",
      },
      {
        href: "/update-metadata",
        label: "Update SPL metadata after Token Hub verification",
      },
      {
        href: "/token-creator",
        label: "Create a new SPL token before opening Token Hub",
      },
      {
        href: "/volume-bot",
        label: "Launch a Solana volume bot from verified mint context",
      },
      {
        href: "/holders-maker",
        label: "Grow holders after Token Hub confirms your mint",
      },
      {
        href: "/liquidity-pool-creator",
        label: "Add liquidity before scaling Token Hub campaigns",
      },
      {
        href: "/fast-swap",
        label: "Swap tokens from the Fast Swap tool hub",
      },
    ],
    heroCtaLabel: "Verify mint in Token Hub",
    faqCta: {
      label: "Open Solana Token Hub",
    },
    ogImage: "/og-image.png",
    ogImageAlt: "Token Hub for Solana — verify mint and open creator tools",
  },
  community: {
    key: "community",
    toolName: "Community Signal",
    toolPath: "/community",
    learnMorePath: "/community/learn-more",
    category: "Marketing & Discoverability",
    primaryKeyword: "community signal solana",
    seoTitle: "Community Signal for Solana | Share-Ready Pages",
    pageHeading: "Community Signal for Solana Tokens",
    metaDescription:
      "Community Signal for Solana: generate share-ready token pages with market context, mint links, and SEO-friendly narratives from Token Hub.",
    seoKeywords: [
      "community signal solana",
      "solana token marketing page",
      "share-ready token page",
      "token discoverability solana",
      "community signal guide",
      "solana token storytelling",
    ],
    description:
      "Community Signal on IDX publishes share-ready Solana token pages — readable narratives, market context from public indexers, mint verification, and ecosystem links — so teams have one canonical URL for social, search, and support.",
    summary:
      "This guide explains how to verify your mint in Token Hub, generate a Community Signal page, reuse one canonical URL across campaigns, and keep token storytelling aligned with on-chain identity.",
    primaryUseCase:
      "Use Community Signal when you need a single public token page to pin in Telegram, share on X, and hand to support — instead of scattering DexScreener, explorer, and swap links.",
    audience: [
      "Marketing teams needing one canonical Solana token page for launch announcements",
      "Community managers pinning share-ready links in Telegram and Discord",
      "Founders connecting Token Hub verification to public-facing token storytelling",
      "Support leads reducing mint confusion with structured FAQ and identity links",
    ],
    steps: [
      "Verify your SPL mint in Token Hub and confirm token name, symbol, and status match your launch materials.",
      "Open the Community Signal flow for your verified mint to generate or visit your token's public page.",
      "Review auto-generated narrative sections, market context disclaimers, and ecosystem links before publishing.",
      "Copy the canonical Community Signal URL and reuse it across social posts, docs, partner updates, and support macros.",
      "Re-generate or refresh the page after metadata updates, liquidity changes, or authority revokes so public copy stays accurate.",
    ],
    faq: [
      {
        q: "What is Community Signal on Solana?",
        a: "Community Signal is IDX's share-ready public page format for Solana tokens. It combines readable token context, market-style metrics from public indexers, mint address verification, and links to swap and ecosystem tools in one canonical URL.",
      },
      {
        q: "How is Community Signal different from Token Hub?",
        a: "Token Hub is an operator workflow for mint verification and routing into IDX tools. Community Signal is the public publish layer — a narrative page you share with holders, press, and search engines while Token Hub stays your internal control center.",
      },
      {
        q: "Do I need a wallet to generate a Community Signal page?",
        a: "Mint verification in Token Hub starts without a wallet. Generating or visiting a Community Signal page uses your verified mint context. Individual swap or management actions may still require wallet connection when you leave the page for on-chain tools.",
      },
      {
        q: "What SEO value does Community Signal provide?",
        a: "Community Signal creates structured, crawlable pages with consistent headings, FAQs, and internal links to IDX tools. That helps search and social platforms understand your token context faster than a raw explorer link alone — without guaranteeing rankings.",
      },
      {
        q: "Where does market data on Community Signal pages come from?",
        a: "Public metrics such as spot estimates and liquidity figures come from third-party indexers like Jupiter's token API. Narrative sections may be template-based or AI-assisted from those facts. Nothing on the page is investment, tax, or legal advice.",
      },
      {
        q: "Should Community Signal replace all social posts?",
        a: "No. Social posts should still carry your voice and updates, but they should link to one canonical Community Signal URL so new users land on consistent token context instead of fragmented explorer screenshots.",
      },
      {
        q: "When should I refresh my Community Signal page?",
        a: "Refresh after metadata updates, authority revokes, major liquidity changes, or rebrand events. Re-verify in Token Hub first so the page reflects current on-chain state and your team does not share stale symbols or links.",
      },
    ],
    intentSections: [
      {
        title: "Canonical URLs for Solana token launches",
        body: "Launch teams often scatter links — DexScreener for charts, explorers for mint proof, swap tools for buying, and Notion docs for narrative. Community Signal consolidates that fragmentation into one share-ready page with structured sections search engines and new holders can parse quickly. When every tweet, Telegram pin, and partner email points to the same URL, you reduce wrong-mint clicks and support tickets asking which link is official.",
      },
      {
        title: "Token storytelling with market context",
        body: "Community Signal pages combine human-readable summaries with market-style context sourced from public indexers — spot estimates, liquidity figures, and mint program details with appropriate disclaimers. The goal is faster comprehension: a newcomer understands what the token is, where it trades, and how to verify the mint without reading five separate tabs. Refresh content after major market or metadata changes so story and facts stay aligned.",
      },
      {
        title: "Community Signal and Token Hub together",
        body: "Start in Token Hub to verify the mint you intend to promote, then generate Community Signal from that verified context. Operators stay in Hub for downstream tools — metadata, volume bots, holders — while public audiences see Signal pages. This division keeps internal workflows powerful without exposing operators-only UI to casual visitors who just need a clear token overview.",
      },
      {
        title: "Discoverability without overpromising",
        body: "Structured pages, internal links to IDX launch tools, and consistent FAQ sections improve discoverability foundations — but they do not guarantee viral growth, DexScreener trending, or token performance. Pair Community Signal with liquidity depth, transparent authority revokes, and authentic community engagement. Update FAQs when real holder questions evolve so the page remains useful months after launch.",
      },
    ],
    engineGuide: {
      title: "Community Signal page components",
      intro:
        "Each Community Signal page assembles verification context, narrative content, market disclaimers, and tool links. Understanding components helps teams maintain accurate public pages over time.",
      engines: [
        {
          id: "mint-context",
          label: "Mint verification context",
          body: "Pages anchor to a verified Solana SPL mint from Token Hub. Mint address, program type, and identity fields ground every other section in correct on-chain context.",
        },
        {
          id: "narrative",
          label: "Narrative and FAQ content",
          body: "Readable summaries and FAQ items explain the token for newcomers. Content may blend templates and AI-assisted copy from public facts — always review before wide distribution.",
        },
        {
          id: "market-context",
          label: "Market context panel",
          body: "Spot, liquidity, and related metrics come from third-party indexers with clear sourcing disclaimers. Figures are informational, not trading recommendations.",
        },
        {
          id: "ecosystem-links",
          label: "Ecosystem and tool links",
          body: "Structured links connect visitors to swap paths, charts, and related IDX workflows without hunting scattered URLs. Keep links tested after pool or metadata changes.",
        },
      ],
    },
    riskNotes: [
      "Market metrics on Community Signal pages are third-party estimates — not guarantees of price or liquidity.",
      "Stale pages mislead holders after metadata or pool migrations — refresh after on-chain changes.",
      "AI-assisted narrative sections require human review before major marketing campaigns.",
      "Canonical URL strategy fails if teams simultaneously promote conflicting unofficial pages.",
      "Community Signal improves clarity but does not replace compliant disclosures or investment advice rules.",
    ],
    decisionChecklist: [
      "Mint is verified in Token Hub before generating or sharing Signal URLs.",
      "Token name, symbol, and narrative match current metadata and marketing.",
      "Canonical Community Signal URL is selected for all public channels.",
      "FAQ and ecosystem links are tested on mobile and desktop.",
      "Owner assigned to refresh page after metadata, liquidity, or authority changes.",
    ],
    related: [],
    extraRelatedLinks: [
      {
        href: "/token-hub",
        label: "Verify mint before publishing Signal pages",
      },
      {
        href: "/update-metadata",
        label: "Update on-chain branding before refreshing Signal",
      },
      {
        href: "/volume-bot",
        label: "Boost chart activity linked from Signal pages",
      },
      {
        href: "/holders-maker",
        label: "Grow holders while Signal drives discovery traffic",
      },
      {
        href: "/reaction-booster",
        label: "Lift DexScreener reactions during Signal campaigns",
      },
      {
        href: "/fast-swap",
        label: "Route buyers through Fast Swap from launch copy",
      },
    ],
    heroCtaLabel: "Generate Community Signal page",
    faqCta: {
      label: "Open Community Signal hub",
    },
    faqHeading: "Community Signal FAQ",
    ogImage: "/og-image.png",
    ogImageAlt: "Community Signal for Solana — share-ready token pages",
  },
  "lock-liquidity": {
    key: "lock-liquidity",
    toolName: "Lock Liquidity",
    toolPath: "/lock-liquidity",
    learnMorePath: "/lock-liquidity/learn-more",
    category: "Liquidity Security",
    primaryKeyword: "lock liquidity solana",
    description: "Learn why liquidity locking matters and how lock proofs support trust.",
    summary: "Guide to LP lock operations, timing, and communication.",
    audience: ["Founders", "Liquidity managers"],
    steps: ["Pick LP token and duration.", "Execute lock.", "Share lock proof publicly."],
    faq: [{ q: "Does liquidity lock remove all risk?", a: "No. It helps trust around liquidity access, but market risk remains." }],
    related: ["manage-liquidity-add", "manage-liquidity-remove", "liquidity-pool-creator"],
    ogImage: "/liquidity-pool.png",
  },
  "create-openbook-market": {
    key: "create-openbook-market",
    toolName: "Create OpenBook Market",
    toolPath: "/create-openbook-market",
    learnMorePath: "/create-openbook-market/learn-more",
    category: "Market Infrastructure",
    primaryKeyword: "create openbook market solana",
    seoTitle: "Create OpenBook Market on Solana | Raydium v4 Guide",
    metaDescription:
      "Create an OpenBook market on Solana for Raydium v4 — pick base/quote, set tick and lot size, fund rent, then verify your market ID.",
    seoKeywords: [
      "create openbook market solana",
      "openbook market creator",
      "raydium v4 market",
      "solana orderbook market",
      "openbook tick size lot size",
      "create trading pair solana",
      "openbook market guide",
    ],
    description:
      "Create OpenBook Market on Solana registers an on-chain orderbook pair that Raydium v4 and compatible aggregators can route against. Use this guide when your SPL token is ready for limit-order trading with SOL, USDC, or USDT as the quote asset — after mint, metadata, and authority decisions are stable enough to lock market parameters.",
    summary:
      "This Raydium v4 guide walks through OpenBook market creation on Solana — from picking base and quote tokens and setting tick size and lot size, through rent estimates and wallet confirmation, to verifying your market ID before you add liquidity or announce trading.",
    primaryUseCase:
      "Use Create OpenBook Market when you need a Raydium v4-compatible market ID for your SPL base mint paired with SOL, USDC, or USDT — before pool creation, aggregator routing checks, or public trade links go live.",
    audience: [
      "Token teams launching Raydium v4 pools that require an OpenBook market ID",
      "DEX operators wiring orderbook routing for SPL base and SOL or stablecoin quotes",
      "Founders moving from bonding-curve launches to centralized limit-order trading on Solana",
      "Developers who want a no-code OpenBook market creator instead of manual CLI setup",
    ],
    steps: [
      "Connect your Solana wallet and select the base token (your SPL mint) plus a quote asset — SOL, USDC, or USDT.",
      "Review tick size and lot size defaults for your quote decimals; adjust only when your token price range needs finer granularity.",
      "Check the rent estimate for market accounts, confirm you hold enough SOL for creation plus fees, then submit the signed transaction.",
      "Copy the market ID from the success screen, verify routing on Raydium or your aggregator, and document the address in Token Hub or launch materials before publishing trade links.",
    ],
    faq: [
      {
        q: "When should I create an OpenBook market on Solana?",
        a: "Create the market after your SPL token mint, metadata, and authorities are finalized — and before you need Raydium v4 limit-order routing or a published market ID for integrators. Most teams create the OpenBook market immediately before or alongside their first Raydium liquidity step.",
      },
      {
        q: "Why does Raydium v4 need an OpenBook market?",
        a: "Raydium v4 classic pools reference an OpenBook market for orderbook-based price discovery and swap routing. Without a valid OpenBook market on Solana, you cannot complete the Raydium v4 market setup flow for many standard pool configurations.",
      },
      {
        q: "Which quote tokens and parameters should I choose?",
        a: "IDX supports SOL, USDC, and USDT as quote assets. Pick the quote your launch audience already holds. Tick size is the minimum price increment; lot size is the minimum base quantity step. Use the tool defaults for your decimal pairing, then tune only when your expected price range is extremely low or high. Creation locks rent-exempt SOL in market accounts plus network fees — the UI shows the rent breakdown before you sign.",
      },
      {
        q: "What should I do after the market is created?",
        a: "Verify the market ID on-chain, add Raydium v4 liquidity if that is your next step, publish the market address to your community, and optionally run a volume bot or Token Hub check so discovery surfaces show consistent pair data. No Anchor scripts are required for a standard pair — connect a wallet, confirm parameters, and sign once.",
      },
    ],
    intentSections: [
      {
        title: "OpenBook markets for Raydium v4 launches",
        body: "An OpenBook market on Solana is the orderbook layer many Raydium v4 setups depend on. When you create OpenBook market records for your base and quote mints, you give routers a canonical market ID for limit orders, spreads, and swap paths. Bonding-curve and AMM-only launches can skip OpenBook until you graduate to Raydium; once you plan Raydium v4 liquidity with orderbook integration, the market becomes a hard prerequisite. Teams that delay creation often hit blocked pool flows or last-minute parameter mistakes under launch pressure. Treat Create OpenBook Market on Solana as part of your technical checklist — parallel to metadata updates and authority revokes — so routing is valid before users arrive.",
      },
      {
        title: "Parameters, rent, and launch verification",
        body: "Tick size and lot size survive for the life of the market — they are not casual UI toggles. Too coarse a tick produces wide minimum spreads; too fine a tick can waste book depth on dust prices. Lot size must align with how holders actually trade your base token decimals, and on-chain mint decimals (not metadata alone) should drive that choice. Your SPL token is the base; SOL, USDC, or USDT is the quote that sets how prices display. OpenBook allocates several rent-exempt accounts on creation, so fund SOL for rent plus fees before signing. After confirmation, store the market public key next to mint address and pool ID, test that Raydium or your aggregator recognizes the pair, plan liquidity depth so the book is not empty at announce time, and keep community links pointing at one source of truth.",
      },
    ],
    engineGuide: {
      title: "What you create in one OpenBook transaction",
      intro:
        "Each OpenBook market on Solana ties a base mint, quote mint, tick/lot configuration, and rent-exempt account layout together for Raydium v4 and compatible DEX routing. Review these pieces before you approve the wallet dialog.",
      engines: [
        {
          id: "pair-and-sizing",
          label: "Pair selection and order sizing",
          body: "Choose the quote your traders already hold, then confirm tick size (minimum price movement) and lot size (minimum base quantity). IDX pre-fills Raydium-compatible defaults for common SOL and stablecoin pairings so you start from a sane baseline instead of guessing decimals under launch pressure.",
        },
        {
          id: "market-id-and-rent",
          label: "Market ID, rent, and post-create checks",
          body: "The success screen returns a market public key used in Raydium pool setup, analytics, and bot configuration. The estimate panel breaks down rent so underfunded wallets fail in simulation instead of mid-flight. Verify the ID on-chain and in your target router before adding liquidity or sharing trade links.",
        },
      ],
    },
    riskNotes: [
      "Tick size and lot size are difficult to change after launch — simulate with small test values if you are unsure about decimal extremes.",
      "Verify base mint decimals on-chain; metadata decimals alone can misconfigure lot size and cause rejected orders.",
      "Fund enough SOL for rent and fees before signing; failed transactions still consume network fees.",
      "Publishing trade links before Raydium liquidity or routing tests can send users to empty or misrouted books.",
      "OpenBook market creation does not replace legal, market-making, or investment compliance review for your token.",
    ],
    decisionChecklist: [
      "Base mint, quote asset (SOL, USDC, or USDT), and decimals are confirmed on-chain.",
      "Tick size and lot size are reviewed against expected token price range.",
      "Wallet SOL balance covers rent estimate plus transaction fees.",
      "Market ID is saved and verified in Raydium v4 or your target router.",
      "Liquidity, Token Hub, and community links reference the same market address.",
    ],
    related: [],
    extraRelatedLinks: [
      {
        href: "/liquidity-pool-creator",
        label: "Add Raydium liquidity after your market ID is live",
      },
      {
        href: "/volume-bot",
        label: "Seed orderbook activity with a Solana volume bot",
      },
      {
        href: "/token-hub",
        label: "Confirm mint and market context before launch",
      },
      {
        href: "/raydium-volume-bot",
        label: "Open the Raydium-focused volume bot landing",
      },
    ],
    pageHeading: "Create OpenBook Market on Solana — Raydium v4 Guide",
    heroCtaLabel: "Start Raydium v4 market setup",
    faqCta: {
      label: "Open the OpenBook market creator",
    },
    faqHeading: "OpenBook Market FAQ",
    heroIllustration: {
      src: "/og-openbook-market.png",
      alt: "OpenBook market creator preview for Raydium v4 pairs",
      width: 1200,
      height: 630,
    },
    ogImage: "/og-openbook-market.png",
    ogImageAlt: "Create OpenBook Market on Solana — Raydium v4 guide",
  },
  exchange: {
    key: "exchange",
    toolName: "IDX Exchange",
    toolPath: IDX_EXCHANGE_URL,
    externalTool: true,
    learnMorePath: "/exchange/learn-more",
    accentTheme: "violet",
    category: "Future Trading",
    primaryKeyword: "idx exchange sol perps solana multi-chain perpetual futures swap",
    seoTitle:
      "IDX Exchange Guide: SOL Perps, Spot Swap & Multi-Chain Trading",
    metaDescription:
      "Learn IDX Exchange at dex.idxsolana.io — wallet-funded BTC, ETH, and SOL perps, spot Swap, and SOL collateral. Trade on Solana, Base, and more.",
    seoKeywords: [
      "idx exchange",
      "sol perps",
      "solana perpetual futures",
      "btc eth sol perps",
      "multi-chain dex",
      "wallet-funded trading",
      "idx exchange guide",
      "dex idxsolana",
    ],
    description:
      "IDX Exchange is a wallet-funded trading app at dex.idxsolana.io — deep shared liquidity, CEX-grade orderbook execution, 100+ USDC perpetual markets including BTC, ETH, and SOL, multi-collateral deposits including SOL, USDC, and ETH, and spot Swap in the same interface. Trade from Solana first, plus Base, Arbitrum, Optimism, Ethereum, BNB Chain, Avalanche, Mantle, and additional networks.",
    summary:
      "IDX Exchange brings professional perpetual futures and spot swaps into one self-custody app. Trade BTC, ETH, and SOL perps long or short with cross or isolated margin, fund with SOL and other collateral, leverage from 1x to 100x, and switch to Swap for instant token exchanges — from your Solana wallet or other supported chains including Base, Arbitrum, Optimism, Ethereum, BNB Chain, Avalanche, and Mantle.",
    audience: [
      "Solana traders who want SOL perpetuals, SOL collateral, and a full orderbook from Phantom, Solflare, or another Solana wallet",
      "Perpetual futures traders who want full multi-chain support, deep liquidity, and BTC, ETH, and SOL perps in one terminal",
      "Spot traders who want swaps without leaving the same wallet-funded app",
      "Users who prefer self-custody and on-chain settlement over centralized exchange custody",
      "Active traders who need market, limit, and advanced order types with fast matching",
    ],
    steps: [
      "Open IDX Exchange at dex.idxsolana.io and connect your wallet — Solana (Phantom, Solflare, etc.) or another supported chain.",
      "Deposit collateral — USDC or SOL for perps on Solana, or USDT, ETH, BNB, WBTC, and other supported assets where available on your chain.",
      "For perps: open Trade, pick a market from 100+ USDC contracts (BTC, ETH, SOL, and more), set cross or isolated margin, choose leverage (1x–100x per market limits), and place market, limit, or advanced orders.",
      "For spot: open Swap, select the token pair, review the quote and network fees, then confirm the wallet transaction.",
      "Monitor positions, funding, PnL, and balances from Portfolio; settle or adjust margin before withdrawing profits.",
    ],
    faq: [
      {
        q: "Does IDX Exchange support spot swap or only perpetual futures?",
        a: "Both. Trade is for USDC-settled perpetual futures — long or short on BTC, ETH, SOL, and 100+ markets, with optional leverage including 1x when you want directional exposure without amplification. Swap is a dedicated spot tab for exchanging tokens inside the same app at dex.idxsolana.io.",
      },
      {
        q: "Which chains does IDX Exchange support?",
        a: "IDX Exchange supports wallet access on all integrated chains — starting with Solana, then Base, Arbitrum, Optimism, Ethereum, BNB Smart Chain, Avalanche, Mantle, and additional networks. Deposit SOL and other collateral on Solana, or check the Markets tab for the live list and deposit options on your chain.",
      },
      {
        q: "How is IDX Exchange funded?",
        a: "Trading is wallet-funded and non-custodial. You connect your wallet, deposit SOL, USDC, or other collateral on-chain, and sign each action from your own keys. Funds stay under your control — there is no centralized custody of your trading balance.",
      },
      {
        q: "When should I use Trade vs Swap?",
        a: "Use Trade when you want perpetual futures — go long or short on BTC, ETH, SOL, or other perps, manage funding rates, and use leverage (including 1x). Use Swap when you want a straightforward spot exchange between supported tokens without opening a perp position.",
      },
      {
        q: "What margin modes are available?",
        a: "IDX Exchange supports cross margin (shared collateral across cross-margin positions) and isolated margin (dedicated margin per position so losses are capped to that allocation). You choose the mode when placing each order.",
      },
      {
        q: "What leverage can I use?",
        a: "Leverage is available from 1x up to 100x in integer steps, depending on the market. Maximum leverage varies by symbol — check the Trade panel for per-market limits before sizing a position.",
      },
      {
        q: "What order types does IDX Exchange support?",
        a: "The Trade tab supports market, limit, IOC (immediate-or-cancel), FOK (fill-or-kill), post-only (maker), and reduce-only orders — so you can execute quickly, provide liquidity, or close positions without flipping direction.",
      },
      {
        q: "What can I trade on IDX Exchange?",
        a: "IDX Exchange offers 100+ perpetual futures markets on major crypto assets such as BTC, ETH, and SOL, plus select real-world asset contracts. All perps are quoted and settled in USDC.",
      },
      {
        q: "Why trade perps instead of spot only?",
        a: "Perpetual futures let you profit when prices rise or fall on BTC, ETH, SOL, and other markets, use capital-efficient leverage, and access deeper futures liquidity than many spot venues. Perps have no expiry date. If you only need a token swap, use the Swap tab instead.",
      },
      {
        q: "Does IDX Exchange offer a testnet?",
        a: "Yes. IDX Exchange includes a testnet for practicing Trade and Swap with test tokens — no mainnet funds at risk. Use Solana Devnet or supported EVM testnets, request test USDC from the faucet where available, and try margin, leverage, and order flows. Switch to mainnet at dex.idxsolana.io when you are ready for live trading on Solana and other supported chains.",
      },
    ],
    intentSections: [
      {
        title: "Deep liquidity, one orderbook",
        body: "IDX Exchange connects you to a unified central limit orderbook with off-chain matching and on-chain settlement — CEX-grade speed without giving up self-custody. Shared liquidity means tighter spreads and smoother entries and exits on BTC, ETH, SOL, and integrated markets.",
      },
      {
        title: "Perps and spot in one app",
        body: "Trade tab handles USDC perpetual futures — BTC, ETH, SOL, and 100+ markets across supported networks. Swap tab handles spot token exchanges — move between directional perp trades and simple swaps without switching products or wallets.",
      },
      {
        title: "Wallet-native, every supported chain",
        body: "Connect from Solana first — then Base, Arbitrum, Optimism, Ethereum, BNB Chain, Avalanche, Mantle, and other supported networks. Deposit SOL, USDC, ETH, and other multi-collateral assets where enabled, sign each action yourself, and monitor positions from Portfolio — no traditional exchange account required.",
      },
      {
        title: "Built for active traders",
        body: "Cross and isolated margin on BTC, ETH, SOL, and 100+ perp markets, leverage up to 100x on eligible symbols, and professional order types including limit, market, post-only, and reduce-only. Funding rates, liquidation prices, and PnL are visible before you confirm.",
      },
    ],
    engineGuide: {
      title: "Why trade on IDX Exchange?",
      intro:
        "IDX Exchange combines a professional trading terminal with the protections of self-custody — deep shared liquidity, fast orderbook matching, and multi-chain access. Trade SOL, BTC, and ETH perps from Solana or any supported chain in one app.",
      engines: [
        {
          id: "shared-liquidity",
          label: "Shared liquidity",
          body: "One unified orderbook across the IDX Exchange experience — no fragmented pools. You access the same deep liquidity whether you connect from Solana to trade SOL perps, or from Base, Arbitrum, or another supported chain.",
        },
        {
          id: "clob",
          label: "CEX-grade orderbook",
          body: "Central limit order book with off-chain matching and on-chain settlement. Real-time charts, depth, and order entry designed for active trading sessions.",
        },
        {
          id: "multi-chain",
          label: "Full multi-chain support",
          body: "Connect and trade from Solana first — then Base, Arbitrum, Optimism, Ethereum, BNB Chain, Avalanche, Mantle, and other supported networks. Pick the chain that fits your wallet and collateral, then access BTC, ETH, SOL, and the same markets from the Trade tab.",
        },
        {
          id: "multi-collateral",
          label: "Multi-collateral",
          body: "Fund your account with SOL on Solana, or USDC, USDT, ETH, BNB, WBTC, and other supported collateral types where enabled — flexible deposits without moving everything to a single asset first.",
        },
        {
          id: "self-custody",
          label: "Self-custody",
          body: "Your keys, your signatures. Collateral is deposited on-chain and trading actions require wallet approval — eliminating centralized custody risk from the trading flow.",
        },
        {
          id: "markets",
          label: "100+ USDC perp markets",
          body: "Trade BTC, ETH, SOL, and 100+ major crypto perpetuals plus select real-world asset contracts. All perps are USDC-denominated and settled in USDC for consistent margin accounting.",
        },
      ],
    },
    networkEnvironments: {
      title: "Mainnet and Testnet",
      intro:
        "IDX Exchange runs on live production networks and a dedicated test environment. Use mainnet for real trading; use testnet to learn the terminal before you commit funds.",
      environments: [
        {
          id: "mainnet",
          label: "Mainnet — live trading",
          body: "Production trading at dex.idxsolana.io with real wallet-funded deposits and on-chain settlement. Connect on Solana mainnet for SOL perps and SOL collateral, or on live networks including Arbitrum, Base, Optimism, Ethereum, BNB Smart Chain, Avalanche, Mantle, SEI, Sonic, and Mode. Access BTC, ETH, SOL, and 100+ USDC perpetual markets with shared orderbook liquidity.",
        },
        {
          id: "testnet",
          label: "Testnet — practice mode",
          body: "Practice Trade and Swap with test tokens — no mainnet funds at risk. Connect on Solana Devnet for Solana wallet flows, or on EVM testnets such as Sepolia (Arbitrum, Base, Optimism, Ethereum), Avalanche Fuji, and BNB testnet. Request test USDC from the in-app faucet on supported test networks (limited per account), then try cross and isolated margin, leverage, order types, and Portfolio workflows before going live.",
        },
      ],
      note: "Switch between mainnet and testnet in the IDX Exchange app before connecting your wallet. The UI and market structure match production — only the network and assets differ.",
    },
    riskNotes: [
      "Perpetual futures involve leverage, liquidation risk, and funding payments — only trade with capital you can afford to lose; 1x reduces leverage risk but perps still differ from spot.",
      "Cross margin shares collateral across positions; isolated margin caps loss to assigned margin but still carries liquidation risk if the market moves against you.",
      "Maximum leverage and position limits vary by market — verify symbol limits, margin mode, and liquidation price before confirming each Trade order.",
      "Funding rates and index/mark prices can move quickly; monitor open perp positions during volatile sessions.",
      "Spot swaps on the Swap tab carry slippage and execution risk — review quotes, minimum amounts, and network fees before confirming.",
      "Unsettled PnL must be settled before it becomes withdrawable balance; withdrawing reduces your margin ratio.",
      "Multi-collateral accounts use loan-to-value (LTV) metrics — high LTV on SOL, USDC, or other collateral can trigger auto-conversion to manage risk.",
      "Mainnet trades use real funds on live networks — double-check you are not on testnet before depositing for production trading.",
    ],
    decisionChecklist: [
      "You are on mainnet for live trading or testnet for practice — confirm the environment in the app before depositing.",
      "You picked the right mode: Trade for perps, Swap for spot exchanges.",
      "Wallet is connected on the correct chain for the market, deposit, or swap pair (Solana for SOL collateral and SOL perps).",
      "Market (BTC, ETH, SOL, or other perp) and collateral (SOL, USDC, etc.) are confirmed for your margin mode.",
      "Leverage, margin mode (cross vs isolated), and position size match your risk plan — or 1x if you want minimal leverage.",
      "Order type fits the goal (market for speed, limit for price, reduce-only to close without flipping).",
      "Funding rate, liquidation price, and fees are understood before opening a perp.",
      "For Swap: quote, slippage tolerance, and network fees are reviewed before confirm.",
    ],
    related: [],
    visualBlocks: [
      {
        id: "exchange-trade-ui",
        title: "Professional trading terminal",
        description:
          "Full chart, live order book, and order entry on the Trade tab — with Markets, Portfolio, and Swap one click away. Trade SOL, BTC, and ETH perps with cross or isolated margin, pick leverage, and place market or limit orders from a single screen.",
        imageSrc: "/idx-exchange-learn-more.png",
        imageAlt:
          "IDX Exchange trading interface showing SOL perpetual chart, order book, and buy/sell panel",
        imageSize: "large",
      },
    ],
    faqCta: {
      label: "Start Perp Trade",
      href: IDX_EXCHANGE_URL,
    },
    ogImage: "/idx-exchange-learn-more.png",
    ogImageWidth: 1024,
    ogImageHeight: 502,
    ogImageAlt:
      "IDX Exchange trading terminal showing SOL perpetual chart, order book, and buy/sell panel",
  },
};

function enrichLearnMoreItem(item) {
  const categoryGuides = {
    "Growth Automation": {
      intentA: "Campaign planning",
      bodyA:
        "Use this tool when you need predictable growth operations with measurable execution steps and clear budget control.",
      intentB: "Execution quality",
      bodyB:
        "Success is measured by cleaner campaign output, reduced manual errors, and repeatable weekly operations.",
      checklist: [
        "Growth objective and KPI are defined.",
        "Budget and pacing limits are approved.",
        "Monitoring owner is assigned.",
        "Post-run review criteria are documented.",
      ],
    },
    "Token Creation": {
      intentA: "Pre-launch setup",
      bodyA:
        "Use this when your team is preparing token infrastructure and needs a reliable setup sequence before campaigns start.",
      intentB: "Launch readiness",
      bodyB:
        "Success means token configuration, metadata, and authority strategy are coherent before public exposure.",
      checklist: [
        "Core token parameters are finalized.",
        "Authority model is documented.",
        "Metadata and naming are reviewed.",
        "Launch dependencies are mapped.",
      ],
    },
    Liquidity: {
      intentA: "Market readiness",
      bodyA:
        "Use this tool to improve tradability and reduce friction around pool setup and early execution conditions.",
      intentB: "Stability objective",
      bodyB:
        "Success means smoother execution paths, lower launch friction, and better liquidity decisions over time.",
      checklist: [
        "Pair and pool assumptions are validated.",
        "Capital allocation is approved.",
        "Slippage and volatility risk are reviewed.",
        "Monitoring process is in place.",
      ],
    },
    "Liquidity Management": {
      intentA: "Position management",
      bodyA:
        "Use this for day-to-day LP operations where timing, sizing, and verification must be controlled.",
      intentB: "Operational hygiene",
      bodyB:
        "Success means LP changes are executed safely with clear records and fewer avoidable transaction failures.",
      checklist: [
        "Pool and LP balances are verified.",
        "Execution window is selected.",
        "Slippage expectations are set.",
        "Post-change validation is planned.",
      ],
    },
    "Security & Trust": {
      intentA: "Trust controls",
      bodyA:
        "Use this when project maturity requires visible trust actions that communities can verify on-chain.",
      intentB: "Confidence objective",
      bodyB:
        "Success means governance signals are clear and holders can validate that critical powers are reduced.",
      checklist: [
        "Authority state is confirmed.",
        "Irreversibility impact is understood.",
        "Proof links are prepared.",
        "Public communication draft is ready.",
      ],
    },
    Distribution: {
      intentA: "Batch operations",
      bodyA:
        "Use this for token distribution workflows where manual transfers are error-prone and time-consuming.",
      intentB: "Delivery confidence",
      bodyB:
        "Success means reliable recipient delivery with auditable transaction output and minimal manual overhead.",
      checklist: [
        "Recipient list is validated.",
        "Amounts and decimals are checked.",
        "Fee budget is sufficient.",
        "Confirmation review is assigned.",
      ],
    },
    "Token Management": {
      intentA: "Lifecycle control",
      bodyA:
        "Use this for token lifecycle adjustments that affect trust, clarity, or operational correctness.",
      intentB: "Signal quality",
      bodyB:
        "Success means users see consistent, accurate token information and confidence signals across channels.",
      checklist: [
        "Change rationale is documented.",
        "Authority and permissions are verified.",
        "On-chain effects are reviewed.",
        "Community update plan is ready.",
      ],
    },
    "Token Operations": {
      intentA: "Workflow consolidation",
      bodyA:
        "Use this when teams need one operational hub instead of fragmented tooling and context switching.",
      intentB: "Coordination objective",
      bodyB:
        "Success means faster execution with fewer mismatches between internal operations and public messaging.",
      checklist: [
        "Token context is complete.",
        "Core workflows are linked.",
        "Ownership and maintenance are assigned.",
        "Periodic review cadence is defined.",
      ],
    },
    "Marketing & Discoverability": {
      intentA: "Canonical messaging",
      bodyA:
        "Use this when launch communication is fragmented and you need one canonical page for share and search.",
      intentB: "Discovery objective",
      bodyB:
        "Success means clearer indexing signals, stronger message consistency, and better new-user comprehension.",
      checklist: [
        "Canonical URL strategy is defined.",
        "Narrative and FAQ are launch-ready.",
        "Internal links to core tools are present.",
        "Campaign distribution plan is aligned.",
      ],
    },
    "Liquidity Security": {
      intentA: "Security signaling",
      bodyA:
        "Use this to provide visible confidence around liquidity commitments and operational transparency.",
      intentB: "Trust objective",
      bodyB:
        "Success means holders can verify lock conditions and understand liquidity access constraints.",
      checklist: [
        "Lock parameters are verified.",
        "On-chain proof path is tested.",
        "Public comms explain lock context.",
        "Review reminders are scheduled.",
      ],
    },
    "Market Infrastructure": {
      intentA: "Trading infrastructure setup",
      bodyA:
        "Use this when you are preparing orderbook-based trading rails and need reliable market creation flow.",
      intentB: "Infrastructure objective",
      bodyB:
        "Success means market params are valid, routing is functional, and launch tooling is ready.",
      checklist: [
        "Base/quote selection is validated.",
        "Market parameters are reviewed.",
        "Downstream routing is tested.",
        "Launch dependencies are documented.",
      ],
    },
    Trading: {
      intentA: "Professional multi-chain trading",
      bodyA:
        "Use IDX Exchange for USDC perpetual futures — BTC, ETH, SOL, and 100+ markets — with shared orderbook liquidity, SOL and multi-collateral funding, cross or isolated margin, and leverage from 1x to 100x — plus spot Swap in the same wallet-funded app at dex.idxsolana.io.",
      intentB: "Risk-aware execution",
      bodyB:
        "Success means you picked Trade vs Swap correctly, chose the right margin mode and order type, and reviewed funding, liquidation, or swap quotes before confirming.",
      checklist: [
        "Trade vs Swap is chosen for the goal.",
        "Chain, market (BTC, ETH, SOL, or other perp), and collateral (SOL, USDC, etc.) are verified.",
        "Margin mode and leverage match your risk plan.",
        "Order type, funding, and exit plan are reviewed before confirm.",
      ],
    },
  };
  const categoryGuide = categoryGuides[item.category];
  return {
    ...item,
    intentSections: item.intentSections || [
      {
        title: categoryGuide ? categoryGuide.intentA : `When to use ${item.toolName}`,
        body: categoryGuide
          ? categoryGuide.bodyA
          : `${item.toolName} is most useful when teams need a repeatable workflow in ${item.category.toLowerCase()} with clearer execution steps and fewer manual mistakes.`,
      },
      {
        title: categoryGuide ? categoryGuide.intentB : "What success looks like",
        body: categoryGuide
          ? categoryGuide.bodyB
          : `Success with ${item.toolName} means cleaner execution quality, clearer communication to your community, and better operational consistency over time.`,
      },
    ],
    riskNotes: item.riskNotes || [
      `Plan budgets, timing, and on-chain confirmation checks before running ${item.toolName}.`,
      `Use small validation runs first, then scale only after outputs look correct.`,
      "Publish transparent updates to your community for trust and expectation management.",
    ],
    decisionChecklist:
      item.decisionChecklist ||
      categoryGuide?.checklist || [
        "Objective for this campaign is clearly defined.",
        "Wallet authority and required permissions are verified.",
        "Budget, fees, and transaction conditions are reviewed.",
        "A monitoring plan exists for post-launch adjustments.",
      ],
  };
}

const ENRICHED_TOOL_LEARN_MORE_CATALOG = Object.fromEntries(
  Object.entries(TOOL_LEARN_MORE_CATALOG).map(([key, value]) => [key, enrichLearnMoreItem(value)]),
);

export const TOOL_LEARN_MORE_LIST = Object.values(ENRICHED_TOOL_LEARN_MORE_CATALOG);

export function getLearnMoreByKey(key) {
  return ENRICHED_TOOL_LEARN_MORE_CATALOG[key] ?? null;
}

export function getLearnMoreByPath(pathname) {
  if (!pathname) return null;

  const normalizePath = (value) => {
    if (!value) return "/";
    const cleaned = value.split("?")[0].split("#")[0];
    if (cleaned === "/") return "/";
    return cleaned.endsWith("/") ? cleaned.slice(0, -1) : cleaned;
  };

  const normalizedPath = normalizePath(pathname);
  const exact = TOOL_LEARN_MORE_LIST.find(
    (item) => normalizePath(item.toolPath) === normalizedPath,
  );
  if (exact) return exact;

  const volumeBotLanding = getVolumeBotLandingConfigByPath(normalizedPath);
  if (volumeBotLanding) {
    const volumeBotGuide = getLearnMoreByKey("volume-bot");
    if (!volumeBotGuide) return null;
    const toolName =
      volumeBotLanding.variant === "default"
        ? volumeBotGuide.toolName
        : volumeBotLanding.hero.h1Primary;
    return { ...volumeBotGuide, toolName };
  }

  return null;
}
